import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../ui/alert-dialog';
import { toast } from 'sonner';
import { FileText, Stamp, Loader2, Mail, Save } from 'lucide-react';
import {
  initialFormData,
  preventWheelChange,
  BalajiConfirmDialog,
  ErrorDialog
} from '../silica-loads';
import './loadDialogStyles';
import { useInvoiceGeneration } from './hooks/useInvoiceGeneration';
import MobileCarouselView from './MobileCarouselView';
import DesktopStackedCardsView from './DesktopStackedCardsView';
import SectionEditModeView from './SectionEditModeView';
import { getTodayString, dateToString } from '../../utils/dateUtils';

const API = `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/api\/?$/, '')}/api`;

export function AddEditLoadDialog({ open, editingLoad, isApprover, onSaved, onClose, initialSection = null, adminOverride = false }) {
  const [formData, setFormData] = useState(initialFormData);
  const [errors, setErrors] = useState({});
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [permitFile, setPermitFile] = useState(null);
  const [otherBills, setOtherBills] = useState([]);
  const [deliveryAttachments, setDeliveryAttachments] = useState([]);
  const [existingDeliveryFiles, setExistingDeliveryFiles] = useState([]);
  const [updatedInvoiceFile, setUpdatedInvoiceFile] = useState(null);
  const [additionInvoiceFile, setAdditionInvoiceFile] = useState(null);
  const [deductionInvoiceFile, setDeductionInvoiceFile] = useState(null);
  const [labReportFile, setLabReportFile] = useState(null);
  const [showBalajiConfirm, setShowBalajiConfirm] = useState(false);
  const [balajiConfirmShown, setBalajiConfirmShown] = useState(false);
  const [errorDialog, setErrorDialog] = useState({ open: false, message: '', fieldName: '', fieldId: '' });
  
  // Confirmation dialog for submit (when subsequent sections will be locked)
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  
  // No changes dialog for edit mode
  const [showNoChangesDialog, setShowNoChangesDialog] = useState(false);
  
  // Track original form data for detecting changes in edit mode
  const [originalFormData, setOriginalFormData] = useState(null);
  
  // Multiple invoices support (BALAJI and SVE)
  const [invoiceFile1, setInvoiceFile1] = useState(null);
  const [invoiceFile2, setInvoiceFile2] = useState(null);
  const [permitFile1, setPermitFile1] = useState(null);
  const [permitFile2, setPermitFile2] = useState(null);
  const [otherBills1, setOtherBills1] = useState([]);
  const [otherBills2, setOtherBills2] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  
  // Slide navigation for Add mode
  const [currentSlideIndex, setCurrentSlideIndex] = useState(0);
  const [isMobile, setIsMobile] = useState(false);
  const [swipeOffset, setSwipeOffset] = useState(0);
  const [isSwiping, setIsSwiping] = useState(false);
  const carouselRef = useRef(null);
  const dialogContentRef = useRef(null);
  const scrollAnchorRef = useRef(null);
  
  // Desktop stacked cards - active section index
  const [desktopActiveSection, setDesktopActiveSection] = useState(0);
  
  // Ordered list of all sections (removed other_bills - now part of invoice)
  // Order: Basic Info → Invoice Details → Delivery & Status → ...
  const ALL_SECTIONS = ['basic', 'invoice', 'status', 'updated_invoice', 'weight_adjustment', 'billing', 'payment'];
  
  // Mobile detection
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Section-only edit mode
  const isSectionMode = !!initialSection;
  const [activeSection, setActiveSection] = useState(initialSection);
  
  // For both add and edit mode - track which section is expanded (null = all collapsed in edit, 'basic' in add)
  const [expandedSection, setExpandedSection] = useState(null);
  
  // Check if we're in edit mode (editing existing load)
  const isEditMode = !!editingLoad?.load_id;
  
  // Update active section when initialSection changes
  useEffect(() => {
    setActiveSection(initialSection);
    // If coming from section edit, expand that section
    if (initialSection) {
      setExpandedSection(initialSection);
    }
  }, [initialSection]);
  
  // Reset expanded section when dialog opens/closes
  useEffect(() => {
    if (open) {
      // In add mode, expand basic section by default
      if (!isEditMode && !initialSection) {
        setExpandedSection('basic');
        setCurrentSlideIndex(0); // Start at first slide for add mode
        setDesktopActiveSection(0);
      }
      // For edit mode, auto-navigation is handled in the form data useEffect (same batch)
    } else {
      setExpandedSection(null);
      setCurrentSlideIndex(0);
      setDesktopActiveSection(0);
    }
  }, [open, isEditMode, initialSection]);
  
  // Helper to check if a section should be shown
  const shouldShowSection = (sectionName) => {
    if (isSectionMode) return activeSection === sectionName;
    
    // Mobile carousel mode (both Add and Edit) - show only current section
    if (isMobile && !isSectionMode) {
      return ALL_SECTIONS[currentSlideIndex] === sectionName;
    }
    
    // Desktop mode - show all enabled sections
    return true;
  };
  
  // Section enable/disable rules
  const isSectionEnabled = (sectionName) => {
    // Section mode - all sections enabled for editing
    if (isSectionMode) return true;
    
    // Progressive unlock rules (both Add and Edit modes)
    // Order: Basic Info (with Date Sent) → Invoice Details → Delivery & Status → ...
    switch (sectionName) {
      case 'basic':
        return true; // Always enabled
      case 'invoice':
        return isBasicInfoComplete(); // Enabled when basic info (including date_sent) is complete
      case 'status':
        // Enabled when invoice details are complete (all mandatory fields)
        // Check for two-invoice mode (BALAJI or SVE with invoice_count=2)
        const isMultiInvoiceClient = formData.client_name === 'BALAJI CASTINGS PVT LTD' || formData.client_name === 'SVE CASTINGS PVT LTD';
        if (isMultiInvoiceClient && Number(formData.invoice_count) === 2) {
          // For edit mode, also check existing uploaded files (manual or generated)
          const hasInvoice1 = !!invoiceFile1 || !!formData.generated_invoice_file_1 || !!editingLoad?.generated_invoice_file_1 || !!editingLoad?.invoice_filename_1;
          const hasInvoice2 = !!invoiceFile2 || !!formData.generated_invoice_file_2 || !!editingLoad?.generated_invoice_file_2 || !!editingLoad?.invoice_filename_2;
          const hasPermit1 = !!permitFile1 || !!editingLoad?.permit_filename_1;
          const hasPermit2 = !!permitFile2 || !!editingLoad?.permit_filename_2;
          return isBasicInfoComplete() && 
                 !!formData.product && 
                 !!formData.kgs_1 && 
                 !!formData.kgs_2 && 
                 !!formData.rate &&
                 hasInvoice1 &&
                 hasInvoice2 &&
                 hasPermit1 &&
                 hasPermit2;
        }
        // Single invoice mode - check for manual upload (invoice_filename) or generated invoice
        return isBasicInfoComplete() && 
               !!formData.product && 
               !!formData.kgs && 
               !!formData.rate &&
               (!!invoiceFile || !!formData.generated_invoice_file || !!editingLoad?.invoice_filename) &&
               (!!permitFile || !!editingLoad?.permit_filename);
      case 'updated_invoice':
        // Only for BALAJI clients - completely hidden for other clients
        if (formData.client_name !== 'BALAJI CASTINGS PVT LTD') return false;
        return formData.acceptance_status === 'accepted';
      case 'weight_adjustment':
        // Only for SVE clients - completely hidden for other clients
        if (formData.client_name !== 'SVE CASTINGS PVT LTD') return false;
        return formData.acceptance_status === 'accepted';
      case 'billing':
        // Enabled only after Updated Invoice (Balaji) or Weight Adjustment (SVE) is complete
        if (formData.client_name === 'BALAJI CASTINGS PVT LTD') {
          // BALAJI: Requires Updated Invoice section to have data (manual or generated)
          return formData.acceptance_status === 'accepted' && 
                 !!formData.total_kgs_updated && 
                 (!!formData.generated_updated_invoice_file || !!formData.updated_invoice_file_url || !!updatedInvoiceFile || !!editingLoad?.updated_invoice_filename);
        } else if (formData.client_name === 'SVE CASTINGS PVT LTD') {
          // SVE with Addition/Deduction: Enable billing when summary invoice is generated
          if (formData.sve_load_type === 'Addition/Deduction') {
            return !!formData.summary_from_date && !!formData.summary_to_date && 
                   !!formData.summary_net_amount && formData.summary_net_amount > 0 &&
                   !!formData.summary_generated_invoice_id;
          }
          // SVE General: Requires Weight Adjustment section to have data
          // No invoice file required - just tons, kgs, and amount
          return formData.acceptance_status === 'accepted' && 
                 !!formData.weight_status && 
                 (formData.weight_status === 'addition' 
                   ? (!!formData.additional_tons && !!formData.additional_kgs && !!formData.additional_invoice_amount)
                   : (!!formData.deduction_tons && !!formData.deduction_kgs && !!formData.deduction_invoice_amount));
        }
        return false; // Not applicable for other clients
      case 'payment':
        // Payment enabled only after Billing section is enabled (which requires Updated Invoice/Weight Adjustment)
        if (formData.client_name === 'BALAJI CASTINGS PVT LTD') {
          return formData.acceptance_status === 'accepted' && 
                 !!formData.total_kgs_updated && 
                 (!!formData.generated_updated_invoice_file || !!formData.updated_invoice_file_url || !!updatedInvoiceFile || !!editingLoad?.updated_invoice_filename);
        } else if (formData.client_name === 'SVE CASTINGS PVT LTD') {
          // SVE with Addition/Deduction: Enable after summary invoice is generated
          if (formData.sve_load_type === 'Addition/Deduction') {
            return !!formData.summary_from_date && !!formData.summary_to_date && 
                   !!formData.summary_net_amount && formData.summary_net_amount > 0 &&
                   !!formData.summary_generated_invoice_id;
          }
          // SVE General: Requires Weight Adjustment section to have data
          return formData.acceptance_status === 'accepted' && 
                 !!formData.weight_status && 
                 (formData.weight_status === 'addition' 
                   ? (!!formData.additional_tons && !!formData.additional_kgs && !!formData.additional_invoice_amount)
                   : (!!formData.deduction_tons && !!formData.deduction_kgs && !!formData.deduction_invoice_amount));
        }
        return false; // Not applicable for other clients
      case 'other_bills':
        return isBasicInfoComplete(); // Enabled when basic info is complete
      default:
        return true;
    }
  };
  
  // Check if a section should be visible at all (client-specific sections)
  const isSectionVisible = (sectionName) => {
    const isSVE = formData.client_name === 'SVE CASTINGS PVT LTD';
    const isAdditionDeduction = formData.sve_load_type === 'Addition/Deduction';
    
    // Special case: SVE + Addition/Deduction only needs basic, billing, payment
    if (isSVE && isAdditionDeduction) {
      switch (sectionName) {
        case 'basic':
          return true;
        case 'billing':
          return true;
        case 'payment':
          return isSectionMode; // Only in view mode
        default:
          return false; // Hide invoice, status, updated_invoice, weight_adjustment
      }
    }
    
    // Normal visibility rules
    switch (sectionName) {
      case 'updated_invoice':
        // Only visible for BALAJI clients
        return formData.client_name === 'BALAJI CASTINGS PVT LTD';
      case 'weight_adjustment':
        // Only visible for SVE clients with General load type
        return isSVE && formData.sve_load_type !== 'Addition/Deduction';
      case 'payment':
        // Payment section only visible in section mode (view mode), not in add/edit dialog
        return isSectionMode;
      default:
        return true; // All other sections are always visible
    }
  };

  // Check if a section should be locked (read-only) in edit mode
  const isSectionLocked = (sectionName) => {
    // Only apply locking logic in edit mode
    if (!editingLoad?.load_id) return false;

    // Admin override: super user opened the load via the "Master Edit" button
    // — bypass ALL section locks, including bill-paid lock.
    if (adminOverride) return false;

    const isBalaji = editingLoad.client_name === 'BALAJI CASTINGS PVT LTD';
    const isSVE = editingLoad.client_name === 'SVE CASTINGS PVT LTD';
    const isBillPaid = editingLoad.bill_status === 'paid';
    
    // Rule: Once bill is paid and saved, ALL sections are locked for ALL clients
    if (isBillPaid) {
      return true; // All sections locked
    }
    
    // Check if invoice details were SAVED (check editingLoad, not formData)
    const isMultiInvoiceClient = isBalaji || isSVE;
    const hasTwoInvoices = Number(editingLoad.invoice_count) === 2;
    const hadInvoiceData = isMultiInvoiceClient && hasTwoInvoices
      ? (!!editingLoad.kgs_1 && !!editingLoad.kgs_2 && !!editingLoad.rate && !!editingLoad.product)
      : (!!editingLoad.kgs && !!editingLoad.rate && !!editingLoad.product);
    
    // Check if updated invoice was SAVED (BALAJI) - check for generated OR manually uploaded file
    const hadUpdatedInvoice = isBalaji && !!editingLoad.total_kgs_updated && 
      (!!editingLoad.generated_updated_invoice_file || !!editingLoad.updated_invoice_file_url || !!editingLoad.updated_invoice_filename);
    
    // Check if weight adjustment was SAVED (SVE) - check editingLoad
    // No invoice file required - just tons, kgs, and amount
    const hadWeightAdjustment = isSVE && !!editingLoad.weight_status && 
      (editingLoad.weight_status === 'addition' 
        ? (!!editingLoad.additional_tons && !!editingLoad.additional_kgs && !!editingLoad.additional_invoice_amount)
        : (!!editingLoad.deduction_tons && !!editingLoad.deduction_kgs && !!editingLoad.deduction_invoice_amount));
    
    // Check if SVE Addition/Deduction summary invoice was generated
    const isSveAdditionDeduction = isSVE && editingLoad.sve_load_type === 'Addition/Deduction';
    const hadSummaryInvoice = isSveAdditionDeduction && !!editingLoad.summary_generated_invoice_id;
    
    switch (sectionName) {
      case 'basic':
        // SVE Addition/Deduction: locked once summary invoice was generated
        if (isSveAdditionDeduction) return hadSummaryInvoice;
        // Rule 1: Basic info locked once invoice details were saved
        return hadInvoiceData;
        
      case 'invoice':
        // Rule 1: Invoice details locked once saved
        return hadInvoiceData;
      
      case 'status':
        // Delivery & Status locks when the next section's data is saved
        // (BALAJI: updated invoice saved, SVE: weight adjustment saved)
        if (isBalaji) return hadUpdatedInvoice;
        if (isSVE) return hadWeightAdjustment;
        return false;
        
      case 'updated_invoice':
        // Rule 2 (BALAJI): Updated invoice locked only after it was saved
        return isBalaji && hadUpdatedInvoice;
        
      case 'weight_adjustment':
        // Rule 3 (SVE): Weight adjustment locked only after it was saved
        return isSVE && hadWeightAdjustment;
        
      default:
        return false; // Billing, Payment not locked until bill is paid
    }
  };
  
  // Helper to check if a section is expanded
  const isSectionExpanded = (sectionName) => {
    // In section mode (from view dialog), show only that section expanded
    if (isSectionMode) return activeSection === sectionName;
    
    // Mobile carousel (both Add and Edit) - current slide is expanded
    if (isMobile && !isSectionMode) {
      return ALL_SECTIONS[currentSlideIndex] === sectionName;
    }
    
    // Desktop: expand the section being focused (clicked)
    return expandedSection === sectionName;
  };
  
  // Toggle section expansion
  const toggleSection = (sectionName) => {
    // In mobile carousel mode, navigate to that section instead
    if (isMobile && !isSectionMode) {
      const index = ALL_SECTIONS.indexOf(sectionName);
      if (index !== -1 && isSectionEnabled(sectionName)) {
        setCurrentSlideIndex(index);
      }
      return;
    }
    setExpandedSection(prev => prev === sectionName ? null : sectionName);
  };
  
  // Refs for sections to scroll to
  const sectionRefs = {
    status: useRef(null),
    basic: useRef(null),
    billing: useRef(null),
    invoice: useRef(null),
    updated_invoice: useRef(null),
    weight_adjustment: useRef(null),
    payment: useRef(null),
    other_bills: useRef(null)
  };
  
  // File removal states
  const [removeInvoiceFile, setRemoveInvoiceFile] = useState(false);
  const [removePermitFile, setRemovePermitFile] = useState(false);
  const [removeUpdatedInvoiceFile, setRemoveUpdatedInvoiceFile] = useState(false);
  const [removeAdditionInvoiceFile, setRemoveAdditionInvoiceFile] = useState(false);
  const [removeDeductionInvoiceFile, setRemoveDeductionInvoiceFile] = useState(false);
  const [removeLabReportFile, setRemoveLabReportFile] = useState(false);
  
  // File input refs for clearing
  const invoiceFileRef = useRef(null);
  const permitFileRef = useRef(null);
  const invoiceFile1Ref = useRef(null);
  const invoiceFile2Ref = useRef(null);
  const permitFile1Ref = useRef(null);
  const permitFile2Ref = useRef(null);

  // Invoice generation hook
  const {
    generating, generating1, generating2, generatingUpdated, generatingAddition, generatingDeduction,
    manualInvoiceErrors, validateManualInvoice, clearManualInvoiceError,
    peekedNumber, peeking, peekNextInvoiceNumber,
    showStampConfirm, showStampConfirm1, showStampConfirm2,
    showStampConfirmUpdated, showStampConfirmAddition, showStampConfirmDeduction,
    setShowStampConfirm, setShowStampConfirm1, setShowStampConfirm2,
    setShowStampConfirmUpdated, setShowStampConfirmAddition, setShowStampConfirmDeduction,
    sendingEmail, showEmailDialog, emailInvoiceData, emailAddress,
    setShowEmailDialog, setEmailAddress, setEmailInvoiceData,
    handleGenerateInvoiceClick, handleGenerateInvoice,
    handleGenerateInvoice1Click, handleGenerateInvoice1,
    handleGenerateInvoice2Click, handleGenerateInvoice2,
    handleGenerateUpdatedInvoiceClick, handleGenerateUpdatedInvoice,
    handleGenerateAdditionInvoiceClick, handleGenerateAdditionInvoice,
    handleGenerateDeductionInvoiceClick, handleGenerateDeductionInvoice,
    handleSendEmailClick, handleSendEmail,
  } = useInvoiceGeneration({
    formData,
    setFormData,
    fileSetters: { setInvoiceFile, setInvoiceFile1, setInvoiceFile2, setUpdatedInvoiceFile, setAdditionInvoiceFile, setDeductionInvoiceFile },
    fileRefs: { invoiceFileRef, invoiceFile1Ref, invoiceFile2Ref }
  });

  // Initialize form when editingLoad changes
  useEffect(() => {
    if (!open) return;
    if (editingLoad) {
      setFormData({
        date: editingLoad.date || '',
        material_from: editingLoad.material_from || '',
        purchase_order: editingLoad.purchase_order || '',
        vehicle_number: editingLoad.vehicle_number || '',
        driver_name: editingLoad.driver_name || '',
        driver_contact: editingLoad.driver_contact || '',
        transport_company: editingLoad.transport_company || '',
        client_name: editingLoad.client_name || '',
        sve_load_type: editingLoad.sve_load_type || (editingLoad.client_name === 'SVE CASTINGS PVT LTD' ? 'General' : ''),
        delivery_status: editingLoad.delivery_status || '',  // Keep empty if not set
        acceptance_status: editingLoad.acceptance_status || '',  // Keep empty if not set
        afs_specification: editingLoad.afs_specification || '',
        date_sent: editingLoad.date_sent || '',
        date_received: editingLoad.date_received || '',
        bill_payment_date: editingLoad.bill_payment_date || '',
        follow_up_email: editingLoad.follow_up_email || '',
        follow_up_emails: editingLoad.follow_up_emails || [],
        follow_up_phone: editingLoad.follow_up_phone || '',
        bill_status: editingLoad.bill_status || '',  // Keep empty if not set
        bill_status_1: editingLoad.bill_status_1 || '',
        bill_payment_date_1: editingLoad.bill_payment_date_1 || '',
        actual_payment_date_1: editingLoad.actual_payment_date_1 || '',
        bill_status_2: editingLoad.bill_status_2 || '',
        bill_payment_date_2: editingLoad.bill_payment_date_2 || '',
        actual_payment_date_2: editingLoad.actual_payment_date_2 || '',
        invoice_number: editingLoad.invoice_number || '',
        invoice_amount: editingLoad.invoice_amount || '',
        tons: editingLoad.tons || '',
        updated_invoice_amount: editingLoad.updated_invoice_amount || '',
        updated_invoice_number: editingLoad.updated_invoice_number || '',
        total_tons: editingLoad.total_tons || '',
        total_kgs_updated: editingLoad.total_kgs_updated || '',
        total_tons_updated: editingLoad.total_tons_updated || (editingLoad.total_kgs_updated ? (parseFloat(editingLoad.total_kgs_updated) / 1000).toFixed(3) : ''),
        delivered_tons: editingLoad.delivered_tons || '',
        pending_tons: editingLoad.pending_tons || '',
        final_invoice_amount: editingLoad.final_invoice_amount || '',
        weight_status: editingLoad.weight_status || '',
        additional_invoice_amount: editingLoad.additional_invoice_amount || '',
        addition_invoice: editingLoad.addition_invoice || '',
        addition_invoice_number: editingLoad.addition_invoice_number || '',
        deduction_invoice_number: editingLoad.deduction_invoice_number || '',
        deduction_tons: editingLoad.deduction_tons || '',
        deduction_kgs: editingLoad.deduction_kgs || '',
        deduction_invoice_amount: editingLoad.deduction_invoice_amount || '',
        additional_tons: editingLoad.additional_tons || '',
        additional_kgs: editingLoad.additional_kgs || '',
        rejection_reason: editingLoad.rejection_reason || '',
        lab_report: editingLoad.lab_report || '',
        payment_due_days: editingLoad.payment_due_days || 45,
        initial_due_days: editingLoad.initial_due_days || '',
        expected_payment_date: editingLoad.expected_payment_date || '',
        actual_payment_date: editingLoad.actual_payment_date || '',
        past_due: editingLoad.past_due || 'no',
        same_as_expected: false,
        // New invoice generation fields
        product: editingLoad.product || 'Silica sand dry',
        kgs: editingLoad.kgs || '',
        rate: editingLoad.rate || '',
        generated_invoice_number: editingLoad.generated_invoice_number || '',
        generated_invoice_amount: editingLoad.generated_invoice_amount || '',
        generated_invoice_file: editingLoad.generated_invoice_file || editingLoad.generated_invoice_file_url || '',
        invoice_file_url: editingLoad.invoice_file_url || '',
        // Invoice count and dual invoice fields (BALAJI)
        invoice_count: editingLoad.invoice_count ? Number(editingLoad.invoice_count) : (editingLoad.kgs_2 ? 2 : 1),
        kgs_1: editingLoad.kgs_1 || '',
        tons_1: editingLoad.tons_1 || '',
        invoice_amount_1: editingLoad.invoice_amount_1 || '',
        generated_invoice_number_1: editingLoad.generated_invoice_number_1 || '',
        generated_invoice_amount_1: editingLoad.generated_invoice_amount_1 || '',
        generated_invoice_file_1: editingLoad.generated_invoice_file_1 || '',
        invoice_file_url_1: editingLoad.invoice_file_url_1 || '',
        kgs_2: editingLoad.kgs_2 || '',
        tons_2: editingLoad.tons_2 || '',
        invoice_amount_2: editingLoad.invoice_amount_2 || '',
        generated_invoice_number_2: editingLoad.generated_invoice_number_2 || '',
        generated_invoice_amount_2: editingLoad.generated_invoice_amount_2 || '',
        generated_invoice_file_2: editingLoad.generated_invoice_file_2 || '',
        invoice_file_url_2: editingLoad.invoice_file_url_2 || '',
        // Updated invoice generation fields (Balaji)
        generated_updated_invoice_number: editingLoad.generated_updated_invoice_number || '',
        generated_updated_invoice_amount: editingLoad.generated_updated_invoice_amount || '',
        generated_updated_invoice_file: editingLoad.generated_updated_invoice_file || editingLoad.updated_invoice_file_url || '',
        // Addition invoice generation fields (SVE)
        generated_addition_invoice_number: editingLoad.generated_addition_invoice_number || '',
        generated_addition_invoice_amount: editingLoad.generated_addition_invoice_amount || '',
        generated_addition_invoice_file: editingLoad.generated_addition_invoice_file || editingLoad.addition_invoice_file_url || '',
        // Deduction invoice generation fields (SVE)
        generated_deduction_invoice_number: editingLoad.generated_deduction_invoice_number || '',
        generated_deduction_invoice_amount: editingLoad.generated_deduction_invoice_amount || '',
        generated_deduction_invoice_file: editingLoad.generated_deduction_invoice_file || editingLoad.deduction_invoice_file_url || '',
        // SVE Summary Invoice fields (Addition/Deduction consolidated)
        summary_generated_invoice_id: editingLoad.summary_generated_invoice_id || '',
        summary_generated_invoice_number: editingLoad.summary_generated_invoice_number || '',
        summary_invoice_amount: editingLoad.summary_invoice_amount || '',
        summary_r2_key: editingLoad.summary_r2_key || '',
        summary_from_date: editingLoad.summary_from_date || '',
        summary_to_date: editingLoad.summary_to_date || '',
        summary_net_amount: editingLoad.summary_net_amount || '',
        summary_net_tons: editingLoad.summary_net_tons || '',
        summary_total_additions: editingLoad.summary_total_additions || 0,
        summary_total_deductions: editingLoad.summary_total_deductions || 0,
        summary_total_addition_tons: editingLoad.summary_total_addition_tons || 0,
        summary_total_deduction_tons: editingLoad.summary_total_deduction_tons || 0,
        // Manual invoice number fields - pre-fill with existing invoice numbers when editing
        manual_invoice_number: editingLoad.generated_invoice_number || editingLoad.manual_invoice_number || '',
        manual_invoice_number_1: editingLoad.generated_invoice_number_1 || editingLoad.manual_invoice_number_1 || '',
        manual_invoice_number_2: editingLoad.generated_invoice_number_2 || editingLoad.manual_invoice_number_2 || '',
        manual_updated_invoice_number: editingLoad.generated_updated_invoice_number || editingLoad.manual_updated_invoice_number || '',
        manual_addition_invoice_number: editingLoad.generated_addition_invoice_number || editingLoad.manual_addition_invoice_number || '',
        manual_deduction_invoice_number: editingLoad.generated_deduction_invoice_number || editingLoad.manual_deduction_invoice_number || '',
        manual_summary_invoice_number: editingLoad.summary_generated_invoice_number || editingLoad.manual_summary_invoice_number || ''
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
    // Reset all file states
    setInvoiceFile(null);
    setPermitFile(null);
    setOtherBills([]);
    // Reset two-invoice mode file states
    setInvoiceFile1(null);
    setInvoiceFile2(null);
    setPermitFile1(null);
    setPermitFile2(null);
    setOtherBills1([]);
    setOtherBills2([]);
    // Reset other file states
    setUpdatedInvoiceFile(null);
    setAdditionInvoiceFile(null);
    setDeductionInvoiceFile(null);
    setLabReportFile(null);
    setDeliveryAttachments([]);
    setExistingDeliveryFiles([]);
    setBalajiConfirmShown(false);
    // Reset file removal states
    setRemoveInvoiceFile(false);
    setRemovePermitFile(false);
    setRemoveUpdatedInvoiceFile(false);
    setRemoveAdditionInvoiceFile(false);
    setRemoveDeductionInvoiceFile(false);
    setRemoveLabReportFile(false);
    
    // Store original form data for edit mode to detect changes
    if (editingLoad) {
      setOriginalFormData(JSON.stringify({
        date: editingLoad.date || '',
        date_sent: editingLoad.date_sent || '',
        material_from: editingLoad.material_from || '',
        purchase_order: editingLoad.purchase_order || '',
        vehicle_number: editingLoad.vehicle_number || '',
        driver_name: editingLoad.driver_name || '',
        driver_contact: editingLoad.driver_contact || '',
        transport_company: editingLoad.transport_company || '',
        client_name: editingLoad.client_name || '',
        sve_load_type: editingLoad.sve_load_type || (editingLoad.client_name === 'SVE CASTINGS PVT LTD' ? 'General' : ''),
        delivery_status: editingLoad.delivery_status || '',  // Keep empty if not set
        acceptance_status: editingLoad.acceptance_status || '',  // Keep empty if not set
        date_received: editingLoad.date_received || '',
        kgs: editingLoad.kgs || '',
        rate: editingLoad.rate || '',
        product: editingLoad.product || 'Silica sand dry',
        // Billing fields
        bill_status: editingLoad.bill_status || '',  // Keep empty if not set
        bill_status_1: editingLoad.bill_status_1 || '',
        bill_payment_date_1: editingLoad.bill_payment_date_1 || '',
        actual_payment_date_1: editingLoad.actual_payment_date_1 || '',
        bill_status_2: editingLoad.bill_status_2 || '',
        bill_payment_date_2: editingLoad.bill_payment_date_2 || '',
        actual_payment_date_2: editingLoad.actual_payment_date_2 || '',
        bill_payment_date: editingLoad.bill_payment_date || '',
        initial_due_days: editingLoad.initial_due_days || 45,
        payment_due_days: editingLoad.payment_due_days || 45,
        internal_reference: editingLoad.internal_reference || '',
        // Updated invoice fields (BALAJI)
        total_kgs_updated: editingLoad.total_kgs_updated || '',
        updated_invoice_amount: editingLoad.updated_invoice_amount || '',
        // Weight adjustment fields (SVE)
        weight_status: editingLoad.weight_status || '',
        additional_tons: editingLoad.additional_tons || '',
        additional_kgs: editingLoad.additional_kgs || '',
        additional_invoice_amount: editingLoad.additional_invoice_amount || '',
        deduction_tons: editingLoad.deduction_tons || '',
        deduction_kgs: editingLoad.deduction_kgs || '',
        deduction_invoice_amount: editingLoad.deduction_invoice_amount || ''
      }));
      
      // Load existing delivery attachments
      if (editingLoad.delivery_attachments && editingLoad.delivery_attachments.length > 0) {
        setExistingDeliveryFiles(editingLoad.delivery_attachments);
      } else {
        setExistingDeliveryFiles([]);
      }
      
      // Auto-navigate to first incomplete section in edit mode (same batch as formData)
      let targetSection = 'billing';
      const isMultiInvoice = (editingLoad.client_name === 'BALAJI CASTINGS PVT LTD' || editingLoad.client_name === 'SVE CASTINGS PVT LTD') && Number(editingLoad.invoice_count) === 2;
      const isSVEAdditionDeduction = editingLoad.client_name === 'SVE CASTINGS PVT LTD' && editingLoad.sve_load_type === 'Addition/Deduction';
      
      // Check if invoice files exist (either generated or manually uploaded)
      const hasInvoiceFile1 = editingLoad.generated_invoice_file_1 || editingLoad.invoice_filename_1 || editingLoad.invoice_file_url_1;
      const hasInvoiceFile2 = editingLoad.generated_invoice_file_2 || editingLoad.invoice_filename_2 || editingLoad.invoice_file_url_2;
      const hasSingleInvoiceFile = editingLoad.invoice_filename || editingLoad.generated_invoice_file || editingLoad.invoice_file_url;
      
      // For SVE Addition/Deduction - simplified navigation
      if (isSVEAdditionDeduction) {
        // Check if summary invoice exists
        if (!editingLoad.summary_generated_invoice_id) {
          targetSection = 'basic';
        } else {
          targetSection = 'billing';
        }
      } else if (!editingLoad.material_from || !editingLoad.vehicle_number || !editingLoad.client_name || !editingLoad.date_sent) {
        targetSection = 'basic';
      } else if (isMultiInvoice 
        ? (!editingLoad.kgs_1 || !editingLoad.kgs_2 || !hasInvoiceFile1 || !hasInvoiceFile2)
        : (!editingLoad.kgs || !hasSingleInvoiceFile)) {
        targetSection = 'invoice';
      } else if (editingLoad.delivery_status !== 'delivered' || editingLoad.acceptance_status !== 'accepted') {
        targetSection = 'status';
      } else if (editingLoad.client_name === 'BALAJI CASTINGS PVT LTD' && 
                 !editingLoad.updated_invoice_amount) {
        targetSection = 'updated_invoice';
      } else if (editingLoad.client_name === 'SVE CASTINGS PVT LTD' && 
                 !editingLoad.weight_status) {
        targetSection = 'weight_adjustment';
      }
      
      const editVisibleSections = ALL_SECTIONS.filter(s => {
        // For SVE Addition/Deduction - only show basic, billing, payment
        if (isSVEAdditionDeduction) {
          return s === 'basic' || s === 'billing' || (s === 'payment' && isSectionMode);
        }
        if (s === 'updated_invoice') return editingLoad.client_name === 'BALAJI CASTINGS PVT LTD';
        if (s === 'weight_adjustment') return editingLoad.client_name === 'SVE CASTINGS PVT LTD';
        if (s === 'payment') return false;
        return true;
      });
      
      const visibleIndex = editVisibleSections.indexOf(targetSection);
      const targetVisibleIndex = visibleIndex >= 0 ? visibleIndex : editVisibleSections.length - 1;
      const targetAllIndex = ALL_SECTIONS.indexOf(targetSection);
      
      setDesktopActiveSection(targetVisibleIndex);
      setCurrentSlideIndex(targetAllIndex >= 0 ? targetAllIndex : 0);
      setExpandedSection(targetSection);
    } else {
      setOriginalFormData(null);
    }
  }, [open, editingLoad]);

  // Clear invoice data when client changes (invoice is client-specific)
  const prevClientRef = useRef(formData.client_name);
  useEffect(() => {
    // Skip on initial mount or when editing
    if (!open || editingLoad) return;
    
    const prevClient = prevClientRef.current;
    const currentClient = formData.client_name;
    
    // If client changed and we had a previous client
    if (prevClient && currentClient && prevClient !== currentClient) {
      // Clear all generated invoice data
      setFormData(prev => ({
        ...prev,
        // Clear single invoice mode data
        generated_invoice_file: '',
        generated_invoice_number: '',
        generated_invoice_amount: '',
        invoice_number: '',
        // Clear two-invoice mode data
        generated_invoice_file_1: '',
        generated_invoice_number_1: '',
        generated_invoice_amount_1: '',
        generated_invoice_file_2: '',
        generated_invoice_number_2: '',
        generated_invoice_amount_2: '',
        // Clear kgs/tons data (different clients may have different requirements)
        kgs: '',
        tons: '',
        invoice_amount: '',
        kgs_1: '',
        tons_1: '',
        invoice_amount_1: '',
        kgs_2: '',
        tons_2: '',
        invoice_amount_2: '',
        // Reset invoice count
        invoice_count: ''
      }));
      
      // Clear all file states
      setInvoiceFile(null);
      setPermitFile(null);
      setInvoiceFile1(null);
      setInvoiceFile2(null);
      setPermitFile1(null);
      setPermitFile2(null);
      setOtherBills([]);
      setOtherBills1([]);
      setOtherBills2([]);
    }
    
    prevClientRef.current = currentClient;
  }, [formData.client_name, open, editingLoad]);

  // Calculate final invoice amount
  useEffect(() => {
    const invoiceAmt = parseFloat(formData.invoice_amount) || 0;
    if (formData.client_name === 'BALAJI CASTINGS PVT LTD' &&
        formData.delivery_status === 'delivered' &&
        formData.acceptance_status === 'accepted') {
      const updatedAmt = parseFloat(formData.updated_invoice_amount) || 0;
      const isTwoInvoice = Number(formData.invoice_count) === 2;
      if (isTwoInvoice) {
        // Dual invoice: final = invoice_amount_1 + updated_invoice_amount
        const invoiceAmt1 = parseFloat(formData.invoice_amount_1) || 0;
        setFormData(prev => ({ ...prev, final_invoice_amount: (invoiceAmt1 + updatedAmt).toFixed(2) }));
      } else {
        setFormData(prev => ({ ...prev, final_invoice_amount: updatedAmt.toString() }));
      }
    } else if (formData.client_name === 'SVE CASTINGS PVT LTD' &&
               formData.delivery_status === 'delivered' &&
               formData.acceptance_status === 'accepted') {
      // Final invoice amount = original invoice amount (don't include addition/deduction)
      setFormData(prev => ({ ...prev, final_invoice_amount: invoiceAmt.toString() }));
    }
  }, [formData.invoice_amount, formData.updated_invoice_amount, formData.invoice_amount_1,
      formData.invoice_count, formData.client_name, formData.delivery_status, formData.acceptance_status]);

  // For SVE: total_tons = original tons (don't include additional/deduction tons)
  useEffect(() => {
    if (formData.client_name === 'SVE CASTINGS PVT LTD' &&
        formData.delivery_status === 'delivered' &&
        formData.acceptance_status === 'accepted') {
      const baseTons = parseFloat(formData.tons) || 0;
      setFormData(prev => ({ ...prev, total_tons: baseTons.toFixed(2) }));
    }
  }, [formData.tons, formData.client_name, formData.delivery_status, formData.acceptance_status]);

  // Auto-calculate invoice amount when kgs and rate change (for single invoice mode)
  useEffect(() => {
    const isMultiInvoiceClient = formData.client_name === 'BALAJI CASTINGS PVT LTD' || formData.client_name === 'SVE CASTINGS PVT LTD';
    const isTwoInvoiceMode = isMultiInvoiceClient && Number(formData.invoice_count) === 2;
    
    // Only auto-calculate for single invoice mode
    if (!isTwoInvoiceMode) {
      const kgs = parseFloat(formData.kgs) || 0;
      const rate = parseFloat(formData.rate) || 0;
      
      if (kgs > 0 && rate > 0) {
        const baseAmount = kgs * rate;
        const gstAmount = baseAmount * 0.05; // 5% GST
        const totalAmount = Math.round(baseAmount + gstAmount); // Round to nearest integer
        
        // Only update if the calculated amount is different
        if (formData.invoice_amount !== totalAmount.toString()) {
          setFormData(prev => ({ ...prev, invoice_amount: totalAmount.toString() }));
        }
      } else if (!formData.kgs || !formData.rate) {
        // Clear invoice_amount when kgs or rate is empty
        if (formData.invoice_amount) {
          setFormData(prev => ({ ...prev, invoice_amount: '' }));
        }
      }
    }
  }, [formData.kgs, formData.rate, formData.client_name, formData.invoice_count]);

  // Auto-calculate invoice amounts for two-invoice mode (kgs_1 and kgs_2)
  useEffect(() => {
    const isMultiInvoiceClient = formData.client_name === 'BALAJI CASTINGS PVT LTD' || formData.client_name === 'SVE CASTINGS PVT LTD';
    const isTwoInvoiceMode = isMultiInvoiceClient && Number(formData.invoice_count) === 2;
    
    if (isTwoInvoiceMode) {
      const rate = parseFloat(formData.rate) || 0;
      
      // Calculate Invoice 1 amount
      const kgs1 = parseFloat(formData.kgs_1) || 0;
      if (kgs1 > 0 && rate > 0) {
        const baseAmount1 = kgs1 * rate;
        const gstAmount1 = baseAmount1 * 0.05;
        const totalAmount1 = Math.round(baseAmount1 + gstAmount1);
        if (formData.invoice_amount_1 !== totalAmount1.toString()) {
          setFormData(prev => ({ ...prev, invoice_amount_1: totalAmount1.toString() }));
        }
      } else if (!formData.kgs_1 || !formData.rate) {
        // Clear invoice_amount_1 when kgs_1 or rate is empty
        if (formData.invoice_amount_1) {
          setFormData(prev => ({ ...prev, invoice_amount_1: '' }));
        }
      }
      
      // Calculate Invoice 2 amount
      const kgs2 = parseFloat(formData.kgs_2) || 0;
      if (kgs2 > 0 && rate > 0) {
        const baseAmount2 = kgs2 * rate;
        const gstAmount2 = baseAmount2 * 0.05;
        const totalAmount2 = Math.round(baseAmount2 + gstAmount2);
        if (formData.invoice_amount_2 !== totalAmount2.toString()) {
          setFormData(prev => ({ ...prev, invoice_amount_2: totalAmount2.toString() }));
        }
      } else if (!formData.kgs_2 || !formData.rate) {
        // Clear invoice_amount_2 when kgs_2 or rate is empty
        if (formData.invoice_amount_2) {
          setFormData(prev => ({ ...prev, invoice_amount_2: '' }));
        }
      }
      
      // Calculate total kgs and invoice_amount (sum of both)
      const totalKgs = kgs1 + kgs2;
      const totalAmount = (parseFloat(formData.invoice_amount_1) || 0) + (parseFloat(formData.invoice_amount_2) || 0);
      setFormData(prev => ({ 
        ...prev, 
        kgs: totalKgs > 0 ? totalKgs.toString() : '',
        invoice_amount: totalAmount > 0 ? totalAmount.toString() : ''
      }));
    }
  }, [formData.kgs_1, formData.kgs_2, formData.rate, formData.client_name, formData.invoice_count, formData.invoice_amount_1, formData.invoice_amount_2]);

  const hasFormData = () => {
    return formData.material_from || formData.vehicle_number || formData.driver_name || formData.client_name || formData.invoice_amount || formData.purchase_order || formData.transport_company;
  };

  // Check if any changes were made in edit mode
  const hasChanges = useCallback(() => {
    if (!isEditMode || !originalFormData) return true; // Always allow for new loads
    
    // Check if form data changed
    const currentSnapshot = JSON.stringify({
      date: formData.date || '',
      date_sent: formData.date_sent || '',
      material_from: formData.material_from || '',
      purchase_order: formData.purchase_order || '',
      vehicle_number: formData.vehicle_number || '',
      driver_name: formData.driver_name || '',
      driver_contact: formData.driver_contact || '',
      transport_company: formData.transport_company || '',
      client_name: formData.client_name || '',
      sve_load_type: formData.sve_load_type || '',
      delivery_status: formData.delivery_status || '',  // Keep empty if not set
      acceptance_status: formData.acceptance_status || '',  // Keep empty if not set
      date_received: formData.date_received || '',
      kgs: formData.kgs || '',
      rate: formData.rate || '',
      product: formData.product || 'Silica sand dry',
      // Billing fields
      bill_status: formData.bill_status || '',  // Keep empty if not set
      bill_status_1: formData.bill_status_1 || '',
      bill_payment_date_1: formData.bill_payment_date_1 || '',
      actual_payment_date_1: formData.actual_payment_date_1 || '',
      bill_status_2: formData.bill_status_2 || '',
      bill_payment_date_2: formData.bill_payment_date_2 || '',
      actual_payment_date_2: formData.actual_payment_date_2 || '',
      bill_payment_date: formData.bill_payment_date || '',
      initial_due_days: formData.initial_due_days || 45,
      payment_due_days: formData.payment_due_days || 45,
      internal_reference: formData.internal_reference || '',
      // Updated invoice fields (BALAJI)
      total_kgs_updated: formData.total_kgs_updated || '',
      updated_invoice_amount: formData.updated_invoice_amount || '',
      // Weight adjustment fields (SVE)
      weight_status: formData.weight_status || '',
      additional_tons: formData.additional_tons || '',
      additional_kgs: formData.additional_kgs || '',
      additional_invoice_amount: formData.additional_invoice_amount || '',
      deduction_tons: formData.deduction_tons || '',
      deduction_kgs: formData.deduction_kgs || '',
      deduction_invoice_amount: formData.deduction_invoice_amount || ''
    });
    
    if (currentSnapshot !== originalFormData) return true;
    
    // Check if any files were added or removed
    if (invoiceFile || permitFile || updatedInvoiceFile || additionInvoiceFile || deductionInvoiceFile) return true;
    if (removeInvoiceFile || removePermitFile || removeUpdatedInvoiceFile || removeAdditionInvoiceFile || removeDeductionInvoiceFile) return true;
    
    return false;
  }, [isEditMode, originalFormData, formData, invoiceFile, permitFile, updatedInvoiceFile, additionInvoiceFile, deductionInvoiceFile, removeInvoiceFile, removePermitFile, removeUpdatedInvoiceFile, removeAdditionInvoiceFile, removeDeductionInvoiceFile]);

  const isBasicInfoComplete = () => {
    // Check all required fields are filled AND driver_contact is exactly 10 digits
    const isDriverContactValid = formData.driver_contact && /^\d{10}$/.test(formData.driver_contact);
    return formData.material_from && formData.date && formData.purchase_order &&
           formData.vehicle_number && formData.driver_name && isDriverContactValid &&
           formData.transport_company && formData.client_name && formData.date_sent;
  };

  const isBalajiAccepted = formData.client_name === 'BALAJI CASTINGS PVT LTD' &&
                            formData.delivery_status === 'delivered' &&
                            formData.acceptance_status === 'accepted';

  const isSveAccepted = formData.client_name === 'SVE CASTINGS PVT LTD' &&
                         formData.delivery_status === 'delivered' &&
                         formData.acceptance_status === 'accepted';

  const isRejected = formData.acceptance_status === 'rejected';

  // Check if a section is complete (for progress indicator)
  const isSectionComplete = useCallback((sectionName) => {
    const isSVE = formData.client_name === 'SVE CASTINGS PVT LTD';
    const isAdditionDeduction = formData.sve_load_type === 'Addition/Deduction';
    
    switch (sectionName) {
      case 'basic':
        // SVE + Addition/Deduction: Complete when dates are entered AND invoice is generated
        if (isSVE && isAdditionDeduction) {
          return !!formData.summary_from_date && !!formData.summary_to_date && 
                 !!formData.summary_net_amount && formData.summary_net_amount > 0 &&
                 !!formData.summary_generated_invoice_id;
        }
        return isBasicInfoComplete(); // Now includes date_sent
      case 'status':
        // Status is complete only when acceptance_status is entered (accepted or rejected)
        return !!formData.acceptance_status && (formData.acceptance_status === 'accepted' || formData.acceptance_status === 'rejected');
      case 'invoice':
        // Check for two-invoice mode (BALAJI or SVE with invoice_count=2)
        const isMultiInvoiceClientComplete = formData.client_name === 'BALAJI CASTINGS PVT LTD' || formData.client_name === 'SVE CASTINGS PVT LTD';
        if (isMultiInvoiceClientComplete && Number(formData.invoice_count) === 2) {
          // For edit mode, also check existing uploaded files (manual or generated)
          const hasInvoice1Complete = !!invoiceFile1 || !!formData.generated_invoice_file_1 || !!editingLoad?.generated_invoice_file_1 || !!editingLoad?.invoice_filename_1;
          const hasInvoice2Complete = !!invoiceFile2 || !!formData.generated_invoice_file_2 || !!editingLoad?.generated_invoice_file_2 || !!editingLoad?.invoice_filename_2;
          const hasPermit1Complete = !!permitFile1 || !!editingLoad?.permit_filename_1;
          const hasPermit2Complete = !!permitFile2 || !!editingLoad?.permit_filename_2;
          return !!formData.product && !!formData.kgs_1 && !!formData.kgs_2 && !!formData.rate && 
                 hasInvoice1Complete &&
                 hasInvoice2Complete &&
                 hasPermit1Complete && hasPermit2Complete;
        }
        // Single invoice mode - check if permit file exists and not removed, check for manual or generated invoice
        const hasPermit = !!permitFile || (!!editingLoad?.permit_filename && !removePermitFile);
        const hasInvoice = !!invoiceFile || !!formData.generated_invoice_file || !!editingLoad?.invoice_filename;
        return !!formData.product && !!formData.kgs && !!formData.rate && hasInvoice && hasPermit;
      case 'updated_invoice':
        // Not applicable for non-Balaji clients or if not accepted
        if (!isBalajiAccepted) return false; // Show as incomplete/N/A, not complete
        const hasUpdatedInvoice = !!updatedInvoiceFile || (!!formData.generated_updated_invoice_file && !removeUpdatedInvoiceFile) || !!editingLoad?.updated_invoice_filename;
        return !!formData.updated_invoice_amount && hasUpdatedInvoice;
      case 'weight_adjustment':
        // Not applicable for non-SVE clients or if not accepted
        if (!isSveAccepted) return false; // Show as incomplete/N/A, not complete
        if (formData.weight_status === 'addition') {
          // Only require tons, kgs, and amount - no invoice file needed
          return !!formData.additional_tons && !!formData.additional_kgs && !!formData.additional_invoice_amount;
        } else if (formData.weight_status === 'deduction') {
          // Only require tons, kgs, and amount - no invoice file needed
          return !!formData.deduction_tons && !!formData.deduction_kgs && !!formData.deduction_invoice_amount;
        }
        return false;
      case 'billing':
        // Billing is complete when bill is paid AND payment date is entered
        if (isSVE && isAdditionDeduction) {
          return formData.bill_status === 'paid' && !!formData.bill_payment_date;
        }
        // SVE dual invoice (split billing): billing section is considered complete (save-allowed)
        // when at least one invoice has been marked paid with its payment date.
        // This lets users save partial payments without being forced to pay both invoices at once.
        if (isSVE && Number(formData.invoice_count) === 2) {
          const invoice1Paid = formData.bill_status_1 === 'paid' && !!formData.bill_payment_date_1;
          const invoice2Paid = formData.bill_status_2 === 'paid' && !!formData.bill_payment_date_2;
          return invoice1Paid || invoice2Paid;
        }
        const needsBilling = isBalajiAccepted || isSveAccepted;
        if (!needsBilling) return false;
        return formData.bill_status === 'paid' && !!formData.bill_payment_date;
      case 'payment':
        // Payment is complete when actual payment date is entered
        if (!formData.date_received) return false;
        return !!formData.actual_payment_date;
      case 'other_bills':
        // Optional - show as not complete initially, complete once something is done
        return false; // Optional sections don't show checkmarks
      default:
        return false;
    }
  }, [formData, invoiceFile, permitFile, invoiceFile1, invoiceFile2, permitFile1, permitFile2, updatedInvoiceFile, additionInvoiceFile, isBalajiAccepted, isSveAccepted, editingLoad, removePermitFile, removeUpdatedInvoiceFile, removeAdditionInvoiceFile]);

  // Handle slide navigation
  const handleSlideChange = useCallback((index) => {
    if (isSectionEnabled(ALL_SECTIONS[index])) {
      setCurrentSlideIndex(index);
      setExpandedSection(ALL_SECTIONS[index]);
    }
  }, [isSectionEnabled]);

  // Auto-advance to next section when current is completed (Add mode only)
  useEffect(() => {
    if (isEditMode || isSectionMode) return;
    
    const currentSection = ALL_SECTIONS[currentSlideIndex];
    if (isSectionComplete(currentSection)) {
      // Find next enabled section that isn't complete
      for (let i = currentSlideIndex + 1; i < ALL_SECTIONS.length; i++) {
        if (isSectionEnabled(ALL_SECTIONS[i]) && !isSectionComplete(ALL_SECTIONS[i])) {
          // Don't auto-advance, just enable the next section
          break;
        }
      }
    }
  }, [formData, currentSlideIndex, isEditMode, isSectionMode, isSectionComplete]);

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'driver_contact':
      case 'follow_up_phone':
        if (value && !/^\d{10}$/.test(value)) error = 'Phone number must be exactly 10 digits';
        break;
      case 'follow_up_email':
        if (value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = 'Please enter a valid email address';
        break;
      case 'invoice_amount':
      case 'updated_invoice_amount':
      case 'additional_invoice_amount':
        if (value && (isNaN(value) || parseFloat(value) < 0)) error = 'Please enter a valid amount';
        break;
      default: break;
    }
    setErrors(prev => ({ ...prev, [name]: error }));
    return error === '';
  };

  const handleFieldChange = (name, value) => {
    // Auto-populate rate based on client
    if (name === 'client_name') {
      let defaultRate = '';
      if (value === 'SVE CASTINGS PVT LTD') {
        defaultRate = '4.350';
      } else if (value === 'BALAJI CASTINGS PVT LTD') {
        defaultRate = '3.80';
      }
      // When client changes, clear ALL invoice-related data since rate changes
      // Also clear kgs/tons to force user to re-enter
      setFormData(prev => ({ 
        ...prev, 
        [name]: value, 
        rate: defaultRate,
        // Clear quantity fields
        kgs: '',
        tons: '',
        kgs_1: '',
        tons_1: '',
        kgs_2: '',
        tons_2: '',
        // Clear invoice amounts (will be recalculated when kgs is entered)
        invoice_amount: '',
        invoice_amount_1: '',
        invoice_amount_2: '',
        // Clear generated invoice data
        generated_invoice_file: '',
        generated_invoice_number: '',
        generated_invoice_amount: '',
        generated_invoice_file_1: '',
        generated_invoice_number_1: '',
        generated_invoice_amount_1: '',
        generated_invoice_file_2: '',
        generated_invoice_number_2: '',
        generated_invoice_amount_2: '',
      }));
      return;
    }

    // Kgs to Tons conversion (1000 kgs = 1 ton)
    if (name === 'kgs') {
      if (!value) { setFormData(prev => ({ ...prev, kgs: '', tons: '' })); return; }
      const kgs = parseFloat(value);
      const tons = (kgs / 1000).toFixed(3);
      setFormData(prev => ({ ...prev, kgs: value, tons: tons }));
      return;
    }

    // Tons to Kgs conversion
    if (name === 'tons') {
      if (!value) { setFormData(prev => ({ ...prev, tons: '', kgs: '' })); return; }
      const tons = parseFloat(value);
      const kgs = (tons * 1000).toFixed(2);
      setFormData(prev => ({ ...prev, tons: value, kgs: kgs }));
      return;
    }

    // Multiple Invoices (BALAJI) - Kgs 1 conversion and total calculation
    if (name === 'kgs_1') {
      if (!value) {
        const kgs2 = parseFloat(formData.kgs_2) || 0;
        const rate = parseFloat(formData.rate) || 0;
        setFormData(prev => ({ ...prev, kgs_1: '', tons_1: '', invoice_amount_1: '', kgs: kgs2 > 0 ? kgs2.toFixed(2) : '', tons: kgs2 > 0 ? (kgs2 / 1000).toFixed(3) : '', invoice_amount: rate > 0 && kgs2 > 0 ? (rate * kgs2 * 1.05).toFixed(2) : '' }));
        return;
      }
      const kgs = parseFloat(value);
      const tons = (kgs / 1000).toFixed(3);
      const rate = parseFloat(formData.rate) || 0;
      const amount = rate > 0 ? (rate * kgs * 1.05).toFixed(2) : '';
      const kgs2 = parseFloat(formData.kgs_2) || 0;
      const totalKgs = (kgs + kgs2).toFixed(2);
      const totalTons = ((kgs + kgs2) / 1000).toFixed(3);
      const totalAmount = rate > 0 ? (rate * (kgs + kgs2) * 1.05).toFixed(2) : '';
      setFormData(prev => ({ 
        ...prev, 
        kgs_1: value, 
        tons_1: tons,
        invoice_amount_1: amount,
        kgs: totalKgs,
        tons: totalTons,
        invoice_amount: totalAmount
      }));
      return;
    }

    // Multiple Invoices (BALAJI) - Tons 1 to Kgs 1 conversion
    if (name === 'tons_1') {
      if (!value) {
        const kgs2 = parseFloat(formData.kgs_2) || 0;
        const rate = parseFloat(formData.rate) || 0;
        setFormData(prev => ({ ...prev, tons_1: '', kgs_1: '', invoice_amount_1: '', kgs: kgs2 > 0 ? kgs2.toFixed(2) : '', tons: kgs2 > 0 ? (kgs2 / 1000).toFixed(3) : '', invoice_amount: rate > 0 && kgs2 > 0 ? (rate * kgs2 * 1.05).toFixed(2) : '' }));
        return;
      }
      const tons = parseFloat(value);
      const kgs = (tons * 1000).toFixed(2);
      const rate = parseFloat(formData.rate) || 0;
      const amount = rate > 0 ? (rate * parseFloat(kgs) * 1.05).toFixed(2) : '';
      const kgs2 = parseFloat(formData.kgs_2) || 0;
      const totalKgs = (parseFloat(kgs) + kgs2).toFixed(2);
      const totalTons = ((parseFloat(kgs) + kgs2) / 1000).toFixed(3);
      const totalAmount = rate > 0 ? (rate * (parseFloat(kgs) + kgs2) * 1.05).toFixed(2) : '';
      setFormData(prev => ({ 
        ...prev, 
        tons_1: value, 
        kgs_1: kgs,
        invoice_amount_1: amount,
        kgs: totalKgs,
        tons: totalTons,
        invoice_amount: totalAmount
      }));
      return;
    }

    // Multiple Invoices (BALAJI) - Kgs 2 conversion and total calculation
    if (name === 'kgs_2') {
      if (!value) {
        const kgs1 = parseFloat(formData.kgs_1) || 0;
        const rate = parseFloat(formData.rate) || 0;
        setFormData(prev => ({ ...prev, kgs_2: '', tons_2: '', invoice_amount_2: '', kgs: kgs1 > 0 ? kgs1.toFixed(2) : '', tons: kgs1 > 0 ? (kgs1 / 1000).toFixed(3) : '', invoice_amount: rate > 0 && kgs1 > 0 ? (rate * kgs1 * 1.05).toFixed(2) : '' }));
        return;
      }
      const kgs = parseFloat(value);
      const tons = (kgs / 1000).toFixed(3);
      const rate = parseFloat(formData.rate) || 0;
      const amount = rate > 0 ? (rate * kgs * 1.05).toFixed(2) : '';
      const kgs1 = parseFloat(formData.kgs_1) || 0;
      const totalKgs = (kgs1 + kgs).toFixed(2);
      const totalTons = ((kgs1 + kgs) / 1000).toFixed(3);
      const totalAmount = rate > 0 ? (rate * (kgs1 + kgs) * 1.05).toFixed(2) : '';
      setFormData(prev => ({ 
        ...prev, 
        kgs_2: value, 
        tons_2: tons,
        invoice_amount_2: amount,
        kgs: totalKgs,
        tons: totalTons,
        invoice_amount: totalAmount
      }));
      return;
    }

    // Multiple Invoices (BALAJI) - Tons 2 to Kgs 2 conversion
    if (name === 'tons_2') {
      if (!value) {
        const kgs1 = parseFloat(formData.kgs_1) || 0;
        const rate = parseFloat(formData.rate) || 0;
        setFormData(prev => ({ ...prev, tons_2: '', kgs_2: '', invoice_amount_2: '', kgs: kgs1 > 0 ? kgs1.toFixed(2) : '', tons: kgs1 > 0 ? (kgs1 / 1000).toFixed(3) : '', invoice_amount: rate > 0 && kgs1 > 0 ? (rate * kgs1 * 1.05).toFixed(2) : '' }));
        return;
      }
      const tons = parseFloat(value);
      const kgs = (tons * 1000).toFixed(2);
      const rate = parseFloat(formData.rate) || 0;
      const amount = rate > 0 ? (rate * parseFloat(kgs) * 1.05).toFixed(2) : '';
      const kgs1 = parseFloat(formData.kgs_1) || 0;
      const totalKgs = (kgs1 + parseFloat(kgs)).toFixed(2);
      const totalTons = ((kgs1 + parseFloat(kgs)) / 1000).toFixed(3);
      const totalAmount = rate > 0 ? (rate * (kgs1 + parseFloat(kgs)) * 1.05).toFixed(2) : '';
      setFormData(prev => ({ 
        ...prev, 
        tons_2: value, 
        kgs_2: kgs,
        invoice_amount_2: amount,
        kgs: totalKgs,
        tons: totalTons,
        invoice_amount: totalAmount
      }));
      return;
    }

    // Multiple Invoices (BALAJI) - Invoice count change
    if (name === 'invoice_count') {
      const newCount = parseInt(value) || 1;
      if (newCount === 1) {
        // Reset invoice 2 fields and use invoice 1 values as totals
        const kgs1 = parseFloat(formData.kgs_1) || 0;
        const rate = parseFloat(formData.rate) || 0;
        const totalAmount = rate > 0 && kgs1 > 0 ? (rate * kgs1 * 1.05).toFixed(2) : formData.invoice_amount;
        setFormData(prev => ({
          ...prev,
          invoice_count: 1,
          kgs_2: '',
          tons_2: '',
          invoice_amount_2: '',
          generated_invoice_number_2: '',
          generated_invoice_amount_2: '',
          generated_invoice_file_2: '',
          kgs: kgs1 > 0 ? kgs1.toFixed(2) : prev.kgs,
          tons: kgs1 > 0 ? (kgs1 / 1000).toFixed(3) : prev.tons,
          invoice_amount: totalAmount
        }));
      } else {
        setFormData(prev => ({ ...prev, invoice_count: 2 }));
      }
      return;
    }

    // Updated Invoice - Total Tons Updated to Total Kgs Updated conversion (BALAJI)
    // REVERTED: total_tons_updated = total_kgs_updated / 1000 (just the updated portion, NOT including kgs_1)
    if (name === 'total_tons_updated') {
      if (!value) { setFormData(prev => ({ ...prev, total_tons_updated: '', total_kgs_updated: '', updated_invoice_amount: '' })); return; }
      const tons = parseFloat(value);
      // Simple conversion: total_kgs_updated = tons * 1000
      const updatedKgs = (tons * 1000).toFixed(2);
      const rate = parseFloat(formData.rate) || 0;
      const updatedAmount = rate > 0 ? (rate * parseFloat(updatedKgs) * 1.05).toFixed(2) : '';
      setFormData(prev => ({ 
        ...prev, 
        total_tons_updated: value, 
        total_kgs_updated: updatedKgs,
        updated_invoice_amount: updatedAmount,
        updated_invoice_number: prev.invoice_number || prev.generated_invoice_number || ''
      }));
      return;
    }

    // Updated Invoice - Total Kgs to Total Tons conversion
    // REVERTED: total_tons_updated = total_kgs_updated / 1000 (just the updated portion, NOT including kgs_1)
    if (name === 'total_kgs_updated') {
      if (!value) { setFormData(prev => ({ ...prev, total_kgs_updated: '', total_tons_updated: '', updated_invoice_amount: '' })); return; }
      const kgs = parseFloat(value);
      // Simple conversion: total_tons_updated = kgs / 1000
      const tons = (kgs / 1000).toFixed(3);
      const rate = parseFloat(formData.rate) || 0;
      const updatedAmount = rate > 0 ? (rate * kgs * 1.05).toFixed(2) : '';
      setFormData(prev => ({ 
        ...prev, 
        total_kgs_updated: value, 
        total_tons_updated: tons,
        updated_invoice_amount: updatedAmount,
        updated_invoice_number: prev.invoice_number || prev.generated_invoice_number || ''
      }));
      return;
    }

    // Addition Invoice - Additional Tons to Additional Kgs conversion (SVE)
    if (name === 'additional_tons') {
      if (!value) { setFormData(prev => ({ ...prev, additional_tons: '', additional_kgs: '', additional_invoice_amount: '' })); return; }
      const tons = parseFloat(value);
      const kgs = (tons * 1000).toFixed(2);
      const rate = parseFloat(formData.rate) || 0;
      const additionalAmount = rate > 0 ? (rate * parseFloat(kgs) * 1.05).toFixed(2) : '';
      setFormData(prev => ({ 
        ...prev, 
        additional_tons: value, 
        additional_kgs: kgs,
        additional_invoice_amount: additionalAmount
      }));
      return;
    }

    // Addition Invoice - Additional Kgs to Additional Tons conversion (SVE)
    if (name === 'additional_kgs') {
      if (!value) { setFormData(prev => ({ ...prev, additional_kgs: '', additional_tons: '', additional_invoice_amount: '' })); return; }
      const kgs = parseFloat(value);
      const tons = (kgs / 1000).toFixed(3);
      const rate = parseFloat(formData.rate) || 0;
      const additionalAmount = rate > 0 ? (rate * kgs * 1.05).toFixed(2) : '';
      setFormData(prev => ({ 
        ...prev, 
        additional_kgs: value, 
        additional_tons: tons,
        additional_invoice_amount: additionalAmount
      }));
      return;
    }

    // Deduction Invoice - Deduction Tons to Deduction Kgs conversion (SVE)
    if (name === 'deduction_tons') {
      if (!value) { setFormData(prev => ({ ...prev, deduction_tons: '', deduction_kgs: '', deduction_invoice_amount: '' })); return; }
      const tons = parseFloat(value);
      const kgs = (tons * 1000).toFixed(2);
      const rate = parseFloat(formData.rate) || 0;
      const deductionAmount = rate > 0 ? (rate * parseFloat(kgs) * 1.05).toFixed(2) : '';
      setFormData(prev => ({ 
        ...prev, 
        deduction_tons: value, 
        deduction_kgs: kgs,
        deduction_invoice_amount: deductionAmount
      }));
      return;
    }

    // Deduction Invoice - Deduction Kgs to Deduction Tons conversion (SVE)
    if (name === 'deduction_kgs') {
      if (!value) { setFormData(prev => ({ ...prev, deduction_kgs: '', deduction_tons: '', deduction_invoice_amount: '' })); return; }
      const kgs = parseFloat(value);
      const tons = (kgs / 1000).toFixed(3);
      const rate = parseFloat(formData.rate) || 0;
      const deductionAmount = rate > 0 ? (rate * kgs * 1.05).toFixed(2) : '';
      setFormData(prev => ({ 
        ...prev, 
        deduction_kgs: value, 
        deduction_tons: tons,
        deduction_invoice_amount: deductionAmount
      }));
      return;
    }

    if (name === 'driver_contact' || name === 'follow_up_phone') {
      value = value.replace(/\D/g, '').slice(0, 10);
    }
    const today = getTodayString();
    // Helper to show error dialog with delay (allows iOS native date picker to close first)
    const showDelayedErrorDialog = (message, fieldName, fieldId) => {
      setTimeout(() => {
        setErrorDialog({ open: true, message, fieldName, fieldId });
      }, 150);
    };
    if ((name === 'date' || name === 'date_sent') && value && value > today) {
      showDelayedErrorDialog(`${name === 'date' ? 'Load Date' : 'Date Sent'} cannot be in the future`, name === 'date' ? 'Load Date' : 'Date Sent', name);
      return;
    }
    if (name === 'date_sent' && value) {
      const dateReceived = formData.date_received;
      if (dateReceived && value > dateReceived) { showDelayedErrorDialog('Date sent cannot be after date received', 'Date Sent', 'date_sent'); return; }
    }
    if (name === 'date_received' && value) {
      const dateSent = formData.date_sent;
      if (dateSent && value < dateSent) { showDelayedErrorDialog('Date received cannot be before date sent', 'Date Received', 'date_received'); return; }
      if (value > today) { showDelayedErrorDialog('Date received cannot be in the future', 'Date Received', 'date_received'); return; }
      const receivedDate = new Date(value);
      const dueDays = (parseInt(formData.initial_due_days) || 45) + 15;
      const expectedDate = new Date(receivedDate.getTime() + (dueDays * 24 * 60 * 60 * 1000));
      setFormData(prev => ({ ...prev, [name]: value, expected_payment_date: dateToString(expectedDate) }));
      validateField(name, value);
      return;
    }
    if (name === 'initial_due_days' && formData.date_received) {
      const receivedDate = new Date(formData.date_received);
      const dueDays = (parseInt(value) || 45) + 15;
      const expectedDate = new Date(receivedDate.getTime() + (dueDays * 24 * 60 * 60 * 1000));
      setFormData(prev => ({ ...prev, initial_due_days: value, expected_payment_date: dateToString(expectedDate) }));
      return;
    }
    if (name === 'delivery_status' && value !== 'delivered') {
      setFormData(prev => ({ ...prev, [name]: value, date_received: '', acceptance_status: 'pending', expected_payment_date: '', actual_payment_date: '', bill_payment_date: '' }));
      return;
    }
    if (name === 'acceptance_status' && (value === 'accepted' || value === 'rejected')) {
      if (formData.delivery_status !== 'delivered') { setErrorDialog({ open: true, message: 'Delivery status must be "Delivered" to change acceptance status', fieldName: 'Acceptance Status', fieldId: 'acceptance_status' }); return; }
      if (!formData.date_received) { setErrorDialog({ open: true, message: 'Please enter date received before changing acceptance status', fieldName: 'Acceptance Status', fieldId: 'acceptance_status' }); return; }
    }
    // SVE dual invoice split billing handlers
    if ((name === 'bill_status_1' || name === 'bill_status_2') && value === 'paid') {
      if (formData.delivery_status !== 'delivered') { setErrorDialog({ open: true, message: 'Delivery status must be "Delivered" before marking bill as paid', fieldName: 'Bill Status', fieldId: name }); return; }
      if (!formData.date_received) { setErrorDialog({ open: true, message: 'Please enter date received before marking bill as paid', fieldName: 'Bill Status', fieldId: name }); return; }
      if (formData.acceptance_status !== 'accepted') { setErrorDialog({ open: true, message: 'Acceptance status must be "Accepted" before marking bill as paid', fieldName: 'Bill Status', fieldId: name }); return; }
      setFormData(prev => ({ ...prev, [name]: value }));
      return;
    }
    if ((name === 'bill_payment_date_1' || name === 'bill_payment_date_2') && value) {
      const suffix = name.endsWith('_1') ? '1' : '2';
      const statusField = `bill_status_${suffix}`;
      if (formData[statusField] !== 'paid') { setErrorDialog({ open: true, message: `Bill status for Invoice ${suffix} must be "Paid" to enter payment date`, fieldName: 'Bill Payment Date', fieldId: name }); return; }
      const paymentDate = new Date(value);
      const dateReceived = formData.date_received ? new Date(formData.date_received) : null;
      if (dateReceived && paymentDate < dateReceived) { setErrorDialog({ open: true, message: 'Payment date must be on or after date received', fieldName: 'Bill Payment Date', fieldId: name }); return; }
      const actualField = `actual_payment_date_${suffix}`;
      setFormData(prev => ({ ...prev, [name]: value, [actualField]: value }));
      return;
    }
    if (name === 'bill_status' && value === 'paid') {
      const isSveAddDed = formData.client_name === 'SVE CASTINGS PVT LTD' && formData.sve_load_type === 'Addition/Deduction';
      if (!isSveAddDed) {
        if (formData.delivery_status !== 'delivered') { setErrorDialog({ open: true, message: 'Delivery status must be "Delivered" before marking bill as paid', fieldName: 'Bill Status', fieldId: 'bill_status' }); return; }
        if (!formData.date_received) { setErrorDialog({ open: true, message: 'Please enter date received before marking bill as paid', fieldName: 'Bill Status', fieldId: 'bill_status' }); return; }
        if (formData.acceptance_status !== 'accepted') { setErrorDialog({ open: true, message: 'Acceptance status must be "Accepted" before marking bill as paid', fieldName: 'Bill Status', fieldId: 'bill_status' }); return; }
        const finalAmount = parseFloat(formData.final_invoice_amount) || 0;
        if (finalAmount <= 0) { setErrorDialog({ open: true, message: 'Final invoice amount must be greater than 0 before marking bill as paid', fieldName: 'Bill Status', fieldId: 'bill_status' }); return; }
      }
      // Set initial_due_days to 0 when bill is paid
      setFormData(prev => ({ ...prev, bill_status: value, initial_due_days: '0' }));
      return;
    }
    if (name === 'bill_payment_date' && value) {
      if (formData.bill_status !== 'paid') { setErrorDialog({ open: true, message: 'Bill status must be "Paid" to enter payment date', fieldName: 'Bill Payment Date', fieldId: 'bill_payment_date' }); return; }
      const paymentDate = new Date(value);
      const dateSent = formData.date_sent ? new Date(formData.date_sent) : null;
      const dateReceived = formData.date_received ? new Date(formData.date_received) : null;
      if (dateSent && paymentDate < dateSent) { setErrorDialog({ open: true, message: 'Payment date must be on or after date sent', fieldName: 'Bill Payment Date', fieldId: 'bill_payment_date' }); return; }
      if (dateReceived && paymentDate < dateReceived) { setErrorDialog({ open: true, message: 'Payment date must be on or after date received', fieldName: 'Bill Payment Date', fieldId: 'bill_payment_date' }); return; }
      setFormData(prev => ({ ...prev, [name]: value, actual_payment_date: value }));
      validateField(name, value);
      return;
    }
    if (name === 'actual_payment_date' && value) {
      const dateReceived = formData.date_received;
      if (dateReceived && value < dateReceived) { setErrorDialog({ open: true, message: 'Actual payment date cannot be before date received', fieldName: 'Actual Payment Date', fieldId: 'actual_payment_date' }); return; }
    }
    if (name === 'same_as_expected') {
      setFormData(prev => ({ ...prev, same_as_expected: value, actual_payment_date: value ? prev.expected_payment_date : prev.actual_payment_date }));
      return;
    }
    if (name === 'total_tons' || name === 'delivered_tons' || name === 'pending_tons') {
      const numValue = parseFloat(value) || 0;
      if (name === 'total_tons') {
        const delivered = parseFloat(formData.delivered_tons) || 0;
        setFormData(prev => ({ ...prev, total_tons: value, pending_tons: delivered > 0 ? Math.max(0, numValue - delivered).toString() : prev.pending_tons }));
        return;
      }
      if (name === 'delivered_tons') {
        const total = parseFloat(formData.total_tons) || 0;
        if (total > 0) { setFormData(prev => ({ ...prev, delivered_tons: value, pending_tons: Math.max(0, total - numValue).toString() })); return; }
      }
      if (name === 'pending_tons') {
        const total = parseFloat(formData.total_tons) || 0;
        if (total > 0) { setFormData(prev => ({ ...prev, pending_tons: value, delivered_tons: Math.max(0, total - numValue).toString() })); return; }
      }
    }
    if (name === 'weight_status') {
      setFormData(prev => ({ ...prev, weight_status: value, additional_tons: '', additional_kgs: '', deduction_tons: '', deduction_kgs: '', total_tons: '', additional_invoice_amount: '', deduction_invoice_amount: '' }));
      return;
    }
    // Clear generated invoice handlers (used by shared InvoiceContent component)
    if (name === '_clear_generated_invoice') {
      setFormData(prev => ({ ...prev, generated_invoice_file: '', generated_invoice_number: '', generated_invoice_amount: '', invoice_number: '', _preview_invoice_data: null }));
      setInvoiceFile(null);
      return;
    }
    if (name === '_clear_gen_inv_1') {
      setFormData(prev => ({ ...prev, generated_invoice_file_1: '', generated_invoice_number_1: '', generated_invoice_amount_1: '', generated_invoice_number: '', invoice_number: '', _preview_invoice_data_1: null }));
      setInvoiceFile1(null);
      return;
    }
    if (name === '_clear_gen_inv_2') {
      setFormData(prev => ({ ...prev, generated_invoice_file_2: '', generated_invoice_number_2: '', generated_invoice_amount_2: '', _preview_invoice_data_2: null }));
      setInvoiceFile2(null);
      return;
    }
    // Clear updated invoice
    if (name === '_clear_updated_invoice') {
      setFormData(prev => ({ ...prev, generated_updated_invoice_file: '', generated_updated_invoice_number: '', generated_updated_invoice_amount: '', updated_invoice_number: '', _preview_updated_invoice_data: null }));
      setUpdatedInvoiceFile(null);
      setRemoveUpdatedInvoiceFile(true);
      return;
    }
    // Clear addition invoice
    if (name === '_clear_addition_invoice') {
      setFormData(prev => ({ ...prev, generated_addition_invoice_file: '', generated_addition_invoice_number: '', generated_addition_invoice_amount: '', addition_invoice_number: '', _preview_addition_invoice_data: null }));
      setAdditionInvoiceFile(null);
      setRemoveAdditionInvoiceFile(true);
      return;
    }
    // Clear deduction invoice
    if (name === '_clear_deduction_invoice') {
      setFormData(prev => ({ ...prev, generated_deduction_invoice_file: '', generated_deduction_invoice_number: '', generated_deduction_invoice_amount: '', deduction_invoice_number: '', _preview_deduction_invoice_data: null }));
      setDeductionInvoiceFile(null);
      setRemoveDeductionInvoiceFile(true);
      return;
    }
    setFormData(prev => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const checkBalajiInvoiceVariance = () => {
    // Removed the 10% variance confirmation popup as per user request
    return true;
  };


  const handleDialogClose = (openState) => {
    if (!openState) {
      if (!editingLoad?.load_id && hasFormData()) {
        const draftData = {
          ...formData,
          invoice_file_name: invoiceFile?.name,
          permit_file_name: permitFile?.name
        };
        onClose(draftData);
      } else {
        cleanupOrphanedSveSummaryInvoice();
        onClose();
      }
    }
  };

  const handleSaveForLater = () => {
    if (hasFormData()) {
      const draftData = {
        ...formData,
        invoice_file_name: invoiceFile?.name,
        permit_file_name: permitFile?.name,
        explicit_save: true
      };
      onClose(draftData);
    } else {
      setErrorDialog({ open: true, message: 'No data to save', fieldName: 'Empty Form', fieldId: '' });
    }
  };

  // Cleanup orphaned SVE summary invoice that was generated but load not submitted
  const cleanupOrphanedSveSummaryInvoice = async () => {
    const newlyGeneratedId = formData.summary_generated_invoice_id;
    // Only delete if it was generated in THIS session (not already saved on the load)
    if (newlyGeneratedId && newlyGeneratedId !== editingLoad?.summary_generated_invoice_id) {
      try {
        await fetch(`${API}/silica-loads/sve-summary-invoice/${newlyGeneratedId}`, {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } catch (err) {
        console.error('Failed to cleanup orphaned SVE summary invoice:', err);
      }
    }
  };

  // Handle cancel button click - show confirmation if there's unsaved data (only for new loads)
  const handleCancelClick = () => {
    // Only show confirmation for new loads (not editing) and if there's data entered
    if (!editingLoad?.load_id && hasFormData()) {
      setShowCancelConfirm(true);
    } else {
      cleanupOrphanedSveSummaryInvoice();
      onClose();
    }
  };

  // Handle cancel confirmation - save as draft
  const handleSaveAsDraft = () => {
    const draftData = {
      ...formData,
      invoice_file_name: invoiceFile?.name,
      permit_file_name: permitFile?.name,
      explicit_save: true
    };
    setShowCancelConfirm(false);
    onClose(draftData);
  };

  // Handle cancel confirmation - discard
  const handleDiscardAndClose = () => {
    setShowCancelConfirm(false);
    cleanupOrphanedSveSummaryInvoice();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return; // Guard against multiple submissions
    
    // For edit mode, check if there are any changes
    if (isEditMode && !hasChanges()) {
      setShowNoChangesDialog(true);
      return;
    }
    
    // Get current active section
    const visibleSections = ALL_SECTIONS.filter(s => isSectionVisible(s));
    const currentActiveSection = isMobile ? ALL_SECTIONS[currentSlideIndex] : visibleSections[desktopActiveSection];
    
    // Only allow submit from specific sections
    // For SVE Addition/Deduction mode, allow submit from basic section when complete
    const isSVEAdditionDeduction = formData.client_name === 'SVE CASTINGS PVT LTD' && formData.sve_load_type === 'Addition/Deduction';
    const allowedSectionsForSubmit = isSVEAdditionDeduction 
      ? ['basic', 'billing'] 
      : ['invoice', 'updated_invoice', 'weight_adjustment', 'billing'];
    
    if (!isSectionMode && !allowedSectionsForSubmit.includes(currentActiveSection)) {
      const sectionNames = isSVEAdditionDeduction 
        ? 'Basic Information or Billing' 
        : 'Invoice, Updated Invoice, Weight Adjustment, or Billing';
      setErrorDialog({ 
        open: true, 
        message: `You can only save from ${sectionNames} sections. Please navigate to the appropriate section.`,
        fieldName: 'Invalid Section',
        fieldId: ''
      });
      return;
    }
    
    // Check if current section is complete (for non-section mode)
    if (!isSectionMode && !isSectionComplete(currentActiveSection)) {
      setErrorDialog({ 
        open: true, 
        message: `Please complete all required fields in "${currentActiveSection === 'basic' ? 'Basic Information' : currentActiveSection === 'invoice' ? 'Invoice Details' : currentActiveSection.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}" section before saving.`,
        fieldName: 'Section Incomplete',
        fieldId: ''
      });
      return;
    }
    
    // Show confirmation dialog before submitting (for both add and edit modes)
    if (!isSectionMode && !showSubmitConfirm) {
      setShowSubmitConfirm(true);
      return;
    }
    
    if (!checkBalajiInvoiceVariance()) return;
    
    setIsSubmitting(true);
    setShowSubmitConfirm(false);

    let hasErrors = false;
    let firstError = null;
    
    // isSVEAdditionDeduction already declared above - reuse it for validation
    
    if (isSVEAdditionDeduction) {
      // For SVE Addition/Deduction, only require the summary invoice to be generated
      if (!formData.summary_generated_invoice_id) {
        setErrors(prev => ({ ...prev, summary_invoice: 'Please generate the summary invoice first' }));
        if (!firstError) firstError = { message: 'Please generate the summary invoice first', fieldName: 'Summary Invoice', fieldId: 'summary_invoice' };
        hasErrors = true;
      }
    } else {
      // Regular validation for other load types

      if (formData.driver_contact && !/^\d{10}$/.test(formData.driver_contact)) {
        setErrors(prev => ({ ...prev, driver_contact: 'Phone number must be exactly 10 digits' }));
        if (!firstError) firstError = { message: 'Phone number must be exactly 10 digits', fieldName: 'Driver Contact', fieldId: 'driver_contact' };
        hasErrors = true;
      }
      
      // Invoice file validation - handle two-invoice mode for BALAJI and SVE
      const isMultiInvoiceClientValidation = (formData.client_name === 'BALAJI CASTINGS PVT LTD' || formData.client_name === 'SVE CASTINGS PVT LTD') && Number(formData.invoice_count) === 2;
      if (isMultiInvoiceClientValidation) {
        // Two invoice mode - check both invoice files (also check existing files when editing)
        if (!invoiceFile1 && !formData.generated_invoice_file_1 && !editingLoad?.generated_invoice_file_1 && !editingLoad?.invoice_filename_1 && !formData.invoice_file_url_1) {
          setErrors(prev => ({ ...prev, invoice_file_1: 'Invoice 1 file is required' }));
          if (!firstError) firstError = { message: 'Invoice 1 file is required', fieldName: 'Invoice 1 File', fieldId: 'invoice_file_1' };
          hasErrors = true;
        }
        if (!invoiceFile2 && !formData.generated_invoice_file_2 && !editingLoad?.generated_invoice_file_2 && !editingLoad?.invoice_filename_2 && !formData.invoice_file_url_2) {
          setErrors(prev => ({ ...prev, invoice_file_2: 'Invoice 2 file is required' }));
          if (!firstError) firstError = { message: 'Invoice 2 file is required', fieldName: 'Invoice 2 File', fieldId: 'invoice_file_2' };
          hasErrors = true;
        }
      } else {
        // Single invoice mode
        if (!invoiceFile && !editingLoad?.invoice_filename && !formData.generated_invoice_file && !formData.invoice_file_url) {
          setErrors(prev => ({ ...prev, invoice_file: 'Invoice file is required' }));
          if (!firstError) firstError = { message: 'Invoice file is required', fieldName: 'Invoice File', fieldId: 'invoice_file' };
          hasErrors = true;
        }
      }
    
      // Permit file validation - handle two-invoice mode for BALAJI and SVE
      if (isMultiInvoiceClientValidation) {
        // Two invoice mode - check both permit files (also check existing files when editing)
        if (!permitFile1 && !editingLoad?.permit_filename_1) {
          setErrors(prev => ({ ...prev, permit_file_1: 'Permit 1 file is required' }));
          if (!firstError) firstError = { message: 'Permit 1 file is required', fieldName: 'Permit 1 File', fieldId: 'permit_file_1' };
          hasErrors = true;
        }
        if (!permitFile2 && !editingLoad?.permit_filename_2) {
          setErrors(prev => ({ ...prev, permit_file_2: 'Permit 2 file is required' }));
          if (!firstError) firstError = { message: 'Permit 2 file is required', fieldName: 'Permit 2 File', fieldId: 'permit_file_2' };
          hasErrors = true;
        }
      } else {
        // Single invoice mode
        if (!permitFile && !editingLoad?.permit_filename) {
          setErrors(prev => ({ ...prev, permit_file: 'Permit file is required' }));
          if (!firstError) firstError = { message: 'Permit file is required', fieldName: 'Permit File', fieldId: 'permit_file' };
          hasErrors = true;
        }
      }
    } // End of regular validation (non SVE Addition/Deduction)
    
    if (formData.delivery_status === 'delivered' && !formData.date_received) {
      setErrors(prev => ({ ...prev, date_received: 'Date received is required when status is Delivered' }));
      if (!firstError) firstError = { message: 'Date received is required when status is Delivered', fieldName: 'Date Received', fieldId: 'date_received' };
      hasErrors = true;
    }
    // Acceptance Status is mandatory when Delivery Status is 'Delivered'
    if (formData.delivery_status === 'delivered' && formData.acceptance_status === 'pending') {
      setErrors(prev => ({ ...prev, acceptance_status: 'Acceptance status must be selected when delivery status is Delivered' }));
      if (!firstError) firstError = { message: 'Acceptance status is required when delivery status is Delivered', fieldName: 'Acceptance Status', fieldId: 'acceptance_status' };
      hasErrors = true;
    }
    if (formData.date_received) {
      const today = getTodayString();
      if (formData.date_sent && formData.date_received < formData.date_sent) {
        setErrors(prev => ({ ...prev, date_received: 'Date received cannot be before date sent' }));
        if (!firstError) firstError = { message: 'Date received must be on or after date sent', fieldName: 'Date Received', fieldId: 'date_received' };
        hasErrors = true;
      }
      if (formData.date_received > today) {
        setErrors(prev => ({ ...prev, date_received: 'Date received cannot be in the future' }));
        if (!firstError) firstError = { message: 'Date received cannot be in the future', fieldName: 'Date Received', fieldId: 'date_received' };
        hasErrors = true;
      }
    }
    // SVE dual-invoice split billing: validate each invoice independently.
    // For split billing, the top-level formData.bill_status is only 'paid' when both are paid,
    // so we skip the legacy overall check when in SVE dual mode and rely on per-invoice validation.
    const isSveDualSplit = formData.client_name === 'SVE CASTINGS PVT LTD' && Number(formData.invoice_count) === 2;
    if (isSveDualSplit) {
      if (formData.bill_status_1 === 'paid') {
        if (!formData.bill_payment_date_1) {
          setErrors(prev => ({ ...prev, bill_payment_date_1: 'Payment date is required for Invoice 1 when bill status is paid' }));
          if (!firstError) firstError = { message: 'Payment date is required for Invoice 1 when bill status is paid', fieldName: 'Invoice 1 Payment Date', fieldId: 'bill_payment_date_1' };
          hasErrors = true;
        }
        if (!formData.actual_payment_date_1) {
          setErrors(prev => ({ ...prev, actual_payment_date_1: 'Actual payment date is required for Invoice 1 when bill status is paid' }));
          if (!firstError) firstError = { message: 'Actual payment date is required for Invoice 1 when bill status is paid', fieldName: 'Invoice 1 Actual Payment Date', fieldId: 'actual_payment_date_1' };
          hasErrors = true;
        }
      }
      if (formData.bill_status_2 === 'paid') {
        if (!formData.bill_payment_date_2) {
          setErrors(prev => ({ ...prev, bill_payment_date_2: 'Payment date is required for Invoice 2 when bill status is paid' }));
          if (!firstError) firstError = { message: 'Payment date is required for Invoice 2 when bill status is paid', fieldName: 'Invoice 2 Payment Date', fieldId: 'bill_payment_date_2' };
          hasErrors = true;
        }
        if (!formData.actual_payment_date_2) {
          setErrors(prev => ({ ...prev, actual_payment_date_2: 'Actual payment date is required for Invoice 2 when bill status is paid' }));
          if (!firstError) firstError = { message: 'Actual payment date is required for Invoice 2 when bill status is paid', fieldName: 'Invoice 2 Actual Payment Date', fieldId: 'actual_payment_date_2' };
          hasErrors = true;
        }
      }
    } else if (formData.bill_status === 'paid') {
      if (!formData.bill_payment_date) {
        setErrors(prev => ({ ...prev, bill_payment_date: 'Payment date is required when bill status is paid' }));
        if (!firstError) firstError = { message: 'Payment date is required when bill status is paid', fieldName: 'Payment Date', fieldId: 'bill_payment_date' };
        hasErrors = true;
      }
      if (!formData.actual_payment_date) {
        setErrors(prev => ({ ...prev, actual_payment_date: 'Actual payment date is required when bill status is paid' }));
        if (!firstError) firstError = { message: 'Actual payment date is required when bill status is paid', fieldName: 'Actual Payment Date', fieldId: 'actual_payment_date' };
        hasErrors = true;
      }
    }
    if (isBalajiAccepted) {
      if (!formData.updated_invoice_amount) {
        setErrors(prev => ({ ...prev, updated_invoice_amount: 'Updated invoice amount is required' }));
        if (!firstError) firstError = { message: 'Updated invoice amount is required', fieldName: 'Updated Invoice Amount', fieldId: 'updated_invoice_amount' };
        hasErrors = true;
      }
      if (!updatedInvoiceFile && !editingLoad?.updated_invoice_filename && !formData.generated_updated_invoice_file) {
        setErrors(prev => ({ ...prev, updated_invoice_file: 'Updated invoice file is required' }));
        if (!firstError) firstError = { message: 'Updated invoice file is required', fieldName: 'Updated Invoice File', fieldId: 'updated_invoice_file' };
        hasErrors = true;
      }
    }
    if (isSveAccepted && formData.weight_status === 'addition') {
      if (!formData.additional_invoice_amount) {
        setErrors(prev => ({ ...prev, additional_invoice_amount: 'Additional invoice amount is required' }));
        if (!firstError) firstError = { message: 'Additional invoice amount is required', fieldName: 'Additional Invoice Amount', fieldId: 'additional_invoice_amount' };
        hasErrors = true;
      }
      // No invoice file validation - it's optional now
    }
    // Also validate deduction when weight_status is 'deduction'
    if (isSveAccepted && formData.weight_status === 'deduction') {
      if (!formData.deduction_invoice_amount) {
        setErrors(prev => ({ ...prev, deduction_invoice_amount: 'Deduction invoice amount is required' }));
        if (!firstError) firstError = { message: 'Deduction invoice amount is required', fieldName: 'Deduction Invoice Amount', fieldId: 'deduction_invoice_amount' };
        hasErrors = true;
      }
      // No invoice file validation - it's optional now
    }
    if (isRejected && !formData.rejection_reason) {
      setErrors(prev => ({ ...prev, rejection_reason: 'Rejection reason is required' }));
      if (!firstError) firstError = { message: 'Rejection reason is required', fieldName: 'Rejection Reason', fieldId: 'rejection_reason' };
      hasErrors = true;
    }

    if (hasErrors) {
      if (firstError) setErrorDialog({ open: true, message: firstError.message, fieldName: firstError.fieldName, fieldId: firstError.fieldId });
      setIsSubmitting(false);
      return;
    }

    const token = localStorage.getItem('token');
    const fd = new FormData();
    
    // Finalize any preview invoices before saving
    // This regenerates the invoices with real sequence numbers and uploads to R2
    let finalizedFormData = { ...formData };
    
    // Finalize preview invoice if exists
    if (formData._preview_invoice_data && formData.generated_invoice_file) {
      try {
        const response = await axios.post(
          `${API}/invoice/generate`,
          { ...formData._preview_invoice_data, preview: false, is_preview_finalize: true, load_id: editingLoad?.load_id || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        finalizedFormData.generated_invoice_number = response.data.invoice_number;
        finalizedFormData.generated_invoice_amount = response.data.invoice_amount.toFixed(2);
        finalizedFormData.generated_invoice_file = response.data.invoice_file_url || '';
        finalizedFormData.invoice_number = response.data.invoice_number;
        finalizedFormData.invoice_amount = response.data.invoice_amount.toFixed(2);
      } catch (error) {
        console.error('Failed to finalize invoice:', error);
        toast.error('Failed to finalize invoice');
        setIsSubmitting(false);
        return;
      }
    }
    
    // Finalize preview Invoice 1 (two-invoice mode) if exists
    if (formData._preview_invoice_data_1 && formData.generated_invoice_file_1) {
      try {
        const response = await axios.post(
          `${API}/invoice/generate`,
          { ...formData._preview_invoice_data_1, preview: false, is_preview_finalize: true, load_id: editingLoad?.load_id || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        finalizedFormData.generated_invoice_number_1 = response.data.invoice_number;
        finalizedFormData.generated_invoice_amount_1 = response.data.invoice_amount.toFixed(2);
        finalizedFormData.generated_invoice_file_1 = response.data.invoice_file_url || '';
      } catch (error) {
        console.error('Failed to finalize invoice 1:', error);
        toast.error('Failed to finalize invoice 1');
        setIsSubmitting(false);
        return;
      }
    }
    
    // Finalize preview Invoice 2 (two-invoice mode) if exists
    if (formData._preview_invoice_data_2 && formData.generated_invoice_file_2) {
      try {
        const response = await axios.post(
          `${API}/invoice/generate`,
          { 
            ...formData._preview_invoice_data_2, 
            preview: false, 
            is_preview_finalize: true,
            load_id: editingLoad?.load_id || null,
            base_invoice_number: finalizedFormData.generated_invoice_number_1 // Use finalized invoice 1 number
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        finalizedFormData.generated_invoice_number_2 = response.data.invoice_number;
        finalizedFormData.generated_invoice_amount_2 = response.data.invoice_amount.toFixed(2);
        finalizedFormData.generated_invoice_file_2 = response.data.invoice_file_url || '';
      } catch (error) {
        console.error('Failed to finalize invoice 2:', error);
        toast.error('Failed to finalize invoice 2');
        setIsSubmitting(false);
        return;
      }
    }
    
    // Finalize preview updated invoice if exists
    if (formData._preview_updated_invoice_data && formData.generated_updated_invoice_file) {
      try {
        const response = await axios.post(
          `${API}/invoice/generate`,
          { ...formData._preview_updated_invoice_data, preview: false, load_id: editingLoad?.load_id || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        finalizedFormData.generated_updated_invoice_number = response.data.invoice_number;
        finalizedFormData.generated_updated_invoice_amount = response.data.invoice_amount.toFixed(2);
        finalizedFormData.generated_updated_invoice_file = response.data.invoice_file_url || '';
        finalizedFormData.updated_invoice_number = response.data.invoice_number;
        finalizedFormData.updated_invoice_amount = response.data.invoice_amount.toFixed(2);
      } catch (error) {
        console.error('Failed to finalize updated invoice:', error);
        toast.error('Failed to finalize updated invoice');
        setIsSubmitting(false);
        return;
      }
    }
    
    // Finalize preview addition invoice if exists
    if (formData._preview_addition_invoice_data && formData.generated_addition_invoice_file) {
      try {
        const response = await axios.post(
          `${API}/invoice/generate-addition`,
          { ...formData._preview_addition_invoice_data, preview: false, load_id: editingLoad?.load_id || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        finalizedFormData.generated_addition_invoice_number = response.data.invoice_number;
        finalizedFormData.generated_addition_invoice_amount = response.data.invoice_amount.toFixed(2);
        finalizedFormData.generated_addition_invoice_file = response.data.invoice_file_url || '';
        finalizedFormData.addition_invoice_number = response.data.invoice_number;
        finalizedFormData.additional_invoice_amount = response.data.invoice_amount.toFixed(2);
      } catch (error) {
        console.error('Failed to finalize addition invoice:', error);
        toast.error('Failed to finalize addition invoice');
        setIsSubmitting(false);
        return;
      }
    }
    
    // Finalize preview deduction invoice if exists
    if (formData._preview_deduction_invoice_data && formData.generated_deduction_invoice_file) {
      try {
        const response = await axios.post(
          `${API}/invoice/generate-deduction`,
          { ...formData._preview_deduction_invoice_data, preview: false, load_id: editingLoad?.load_id || null },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        finalizedFormData.generated_deduction_invoice_number = response.data.invoice_number;
        finalizedFormData.generated_deduction_invoice_amount = response.data.invoice_amount.toFixed(2);
        finalizedFormData.generated_deduction_invoice_file = response.data.invoice_file_url || '';
        finalizedFormData.deduction_invoice_number = response.data.invoice_number;
        finalizedFormData.deduction_invoice_amount = response.data.invoice_amount.toFixed(2);
      } catch (error) {
        console.error('Failed to finalize deduction invoice:', error);
        toast.error('Failed to finalize deduction invoice');
        setIsSubmitting(false);
        return;
      }
    }
    
    // Remove preview data fields from the form data
    delete finalizedFormData._preview_invoice_data;
    delete finalizedFormData._preview_invoice_data_1;
    delete finalizedFormData._preview_invoice_data_2;
    delete finalizedFormData._preview_updated_invoice_data;
    delete finalizedFormData._preview_addition_invoice_data;
    delete finalizedFormData._preview_deduction_invoice_data;
    
    // BALAJI dual-invoice: compute overall totals on submit
    // total_tons = (kgs_1 + total_kgs_updated) / 1000, final_invoice_amount = invoice_amount_1 + updated_invoice_amount
    const isBalajiDualInvoice = finalizedFormData.client_name === 'BALAJI CASTINGS PVT LTD' && Number(finalizedFormData.invoice_count) === 2;
    if (isBalajiDualInvoice && finalizedFormData.total_kgs_updated) {
      const kgs1 = parseFloat(finalizedFormData.kgs_1) || 0;
      const kgsUpdated = parseFloat(finalizedFormData.total_kgs_updated) || 0;
      finalizedFormData.kgs = (kgs1 + kgsUpdated).toString();
      finalizedFormData.tons = ((kgs1 + kgsUpdated) / 1000).toFixed(3);
      finalizedFormData.total_tons = ((kgs1 + kgsUpdated) / 1000).toFixed(3);
      const invoiceAmt1 = parseFloat(finalizedFormData.invoice_amount_1 || finalizedFormData.generated_invoice_amount_1) || 0;
      const updatedAmt = parseFloat(finalizedFormData.updated_invoice_amount || finalizedFormData.generated_updated_invoice_amount) || 0;
      finalizedFormData.final_invoice_amount = (invoiceAmt1 + updatedAmt).toFixed(2);
    }
    
    // BALAJI single-invoice: updated tons should override original tons on listing
    const isBalajiSingleInvoice = finalizedFormData.client_name === 'BALAJI CASTINGS PVT LTD' && Number(finalizedFormData.invoice_count) !== 2;
    if (isBalajiSingleInvoice && finalizedFormData.total_kgs_updated) {
      const kgsUpdated = parseFloat(finalizedFormData.total_kgs_updated) || 0;
      finalizedFormData.tons = (kgsUpdated / 1000).toFixed(3);
      finalizedFormData.total_tons = (kgsUpdated / 1000).toFixed(3);
    }
    
    Object.keys(finalizedFormData).forEach(key => {
      if (finalizedFormData[key] !== '' && finalizedFormData[key] !== null && !key.startsWith('_')) fd.append(key, finalizedFormData[key]);
    });
    
    // Handle invoice files - two-invoice mode for BALAJI and SVE
    const isMultiInvoiceClientSubmit = (formData.client_name === 'BALAJI CASTINGS PVT LTD' || formData.client_name === 'SVE CASTINGS PVT LTD') && Number(formData.invoice_count) === 2;
    if (isMultiInvoiceClientSubmit) {
      if (invoiceFile1) fd.append('invoice_file_1', invoiceFile1);
      if (invoiceFile2) fd.append('invoice_file_2', invoiceFile2);
      if (permitFile1) fd.append('permit_file_1', permitFile1);
      if (permitFile2) fd.append('permit_file_2', permitFile2);
      otherBills1.forEach(file => fd.append('other_bills_1', file));
      otherBills2.forEach(file => fd.append('other_bills_2', file));
    } else {
      // Single invoice mode
      if (invoiceFile) fd.append('invoice_file', invoiceFile);
      if (permitFile) fd.append('permit_file', permitFile);
      otherBills.forEach(file => fd.append('other_bills', file));
    }
    
    if (updatedInvoiceFile) fd.append('updated_invoice_file', updatedInvoiceFile);
    if (additionInvoiceFile) fd.append('addition_invoice_file', additionInvoiceFile);
    if (deductionInvoiceFile) fd.append('deduction_invoice_file', deductionInvoiceFile);
    if (labReportFile) fd.append('lab_report_file', labReportFile);
    
    // Delivery attachments
    if (deliveryAttachments && deliveryAttachments.length > 0) {
      deliveryAttachments.forEach((file) => {
        fd.append('delivery_attachment_files', file);
      });
    }
    // Track removed existing delivery files
    if (existingDeliveryFiles) {
      fd.append('existing_delivery_files', JSON.stringify(existingDeliveryFiles));
    }
    
    // Add file removal flags
    if (removeInvoiceFile) fd.append('remove_invoice_file', 'true');
    if (removePermitFile) fd.append('remove_permit_file', 'true');
    if (removeUpdatedInvoiceFile) fd.append('remove_updated_invoice_file', 'true');
    if (removeAdditionInvoiceFile) fd.append('remove_addition_invoice_file', 'true');
    if (removeDeductionInvoiceFile) fd.append('remove_deduction_invoice_file', 'true');
    if (removeLabReportFile) fd.append('remove_lab_report_file', 'true');

    try {
      if (editingLoad?.load_id) {
        await axios.put(`${API}/silica-loads/${editingLoad.load_id}`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Silica load updated');
      } else {
        await axios.post(`${API}/silica-loads`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
        toast.success('Silica load added');
      }
      onSaved();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const errorMsg = typeof detail === 'string' 
        ? detail 
        : Array.isArray(detail) 
          ? detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ')
          : 'Failed to save';
      toast.error(errorMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <BalajiConfirmDialog
        open={showBalajiConfirm}
        onOpenChange={setShowBalajiConfirm}
        invoiceAmount={formData.invoice_amount}
        updatedInvoiceAmount={formData.updated_invoice_amount}
        onCancel={() => setShowBalajiConfirm(false)}
        onConfirm={() => {
          setBalajiConfirmShown(true);
          setShowBalajiConfirm(false);
          setTimeout(() => { document.getElementById('silica-load-form')?.requestSubmit(); }, 100);
        }}
      />

      <ErrorDialog
        open={errorDialog.open}
        onOpenChange={(o) => setErrorDialog(prev => ({ ...prev, open: o }))}
        message={errorDialog.message}
        fieldName={errorDialog.fieldName}
        onScrollToField={() => {
          setErrorDialog(prev => ({ ...prev, open: false }));
          setTimeout(() => {
            const el = document.getElementById(errorDialog.fieldId);
            if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
          }, 200);
        }}
      />

      <Dialog open={open} onOpenChange={(isOpen) => { /* Prevent closing by clicking outside */ }}>
        <DialogContent 
          ref={dialogContentRef}
          className={`max-w-[98vw] ${isSectionMode ? 'sm:max-w-2xl sm:w-auto' : isMobile ? 'w-[98vw]' : 'sm:w-auto sm:max-w-fit p-10'} max-h-[95vh] overflow-y-auto ${isSectionMode || isMobile ? 'overflow-x-hidden' : ''} z-[100] w-[98vw]`}
          onPointerDownOutside={(e) => e.preventDefault()} 
          onEscapeKeyDown={(e) => e.preventDefault()}
          hideCloseButton={true}
          mobileFullHeight={!isSectionMode && isMobile}
        >
          {/* Scroll anchor: position:absolute top:0 directly inside DialogContent (the scroll container).
              offsetParent = DialogContent (position:fixed). offsetTop = 0.
              scrollIntoView on this always sets DialogContent.scrollTop = 0.
              Must be here — NOT inside any animated element (transform creates new containing block). */}
          {isMobile && !isSectionMode && (
            <div ref={scrollAnchorRef} style={{ position: 'absolute', top: 0, height: 0, width: 0, overflow: 'hidden', pointerEvents: 'none' }} />
          )}
          {/* Hide header in mobile Add mode and desktop stacked cards mode */}
          {!(!isSectionMode && isMobile) && isSectionMode && (
          <DialogHeader>
            <DialogTitle className="text-xl font-bold" style={{ fontFamily: "'Manrope', sans-serif" }}>
              {isSectionMode ? (
                `Edit ${activeSection === 'basic' ? 'Basic Information' : 
                        activeSection === 'status' ? 'Delivery Status' : 
                        activeSection === 'invoice' ? 'Invoice Details' : 
                        activeSection === 'updated_invoice' ? 'Updated Invoice' : 
                        activeSection === 'weight_adjustment' ? 'Weight Adjustment' : 
                        activeSection === 'payment' ? 'Payment Tracking' :
                        activeSection === 'other_bills' ? 'Other Bills' : 'Section'}`
              ) : null}
            </DialogTitle>
          </DialogHeader>
          )}
          
          <form id="silica-load-form" onSubmit={handleSubmit} className={`load-dialog-enter ${!isSectionMode && isMobile ? 'mt-0' : !isSectionMode && !isMobile ? 'mt-1 space-y-3' : 'mt-4 space-y-4'}`} style={!isSectionMode && !isMobile ? { width: '100%' } : { width: '100%', maxWidth: '100%', overflowX: 'hidden' }}>
            {/* Mobile Carousel Mode (Add and Edit) - Using extracted component */}
            {!isSectionMode && isMobile ? (() => {
              // Build context value for MobileCarouselView
              const mobileContextValue = {
                formData, setFormData, handleFieldChange, errors,
                editingLoad, isEditMode, isSubmitting, setErrorDialog,
                ALL_SECTIONS, currentSlideIndex, handleSlideChange,
                isSectionEnabled, isSectionComplete, isSectionLocked, isSectionVisible,
                handleCancelClick,
                // Invoice files
                invoiceFile, setInvoiceFile, permitFile, setPermitFile, otherBills, setOtherBills,
                invoiceFile1, setInvoiceFile1, invoiceFile2, setInvoiceFile2,
                permitFile1, setPermitFile1, permitFile2, setPermitFile2,
                otherBills1, setOtherBills1, otherBills2, setOtherBills2,
                // Updated/Addition/Deduction files
                updatedInvoiceFile, setUpdatedInvoiceFile,
                additionInvoiceFile, setAdditionInvoiceFile,
                deductionInvoiceFile, setDeductionInvoiceFile,
                // File removal flags
                removeInvoiceFile, setRemoveInvoiceFile,
                removePermitFile, setRemovePermitFile,
                removeUpdatedInvoiceFile, setRemoveUpdatedInvoiceFile,
                removeAdditionInvoiceFile, setRemoveAdditionInvoiceFile,
                removeDeductionInvoiceFile, setRemoveDeductionInvoiceFile,
                // Invoice generation
                generating, generating1, generating2, generatingUpdated, generatingAddition, generatingDeduction,
                handleGenerateInvoiceClick, handleGenerateInvoice1Click, handleGenerateInvoice2Click,
                handleGenerateUpdatedInvoiceClick, handleGenerateAdditionInvoiceClick, handleGenerateDeductionInvoiceClick,
                handleSendEmailClick,
                dialogContentRef,
                scrollAnchorRef,
                manualInvoiceErrors, clearManualInvoiceError,
                peekedNumber, peeking, peekNextInvoiceNumber,
                deliveryAttachments, setDeliveryAttachments, existingDeliveryFiles, setExistingDeliveryFiles,
              };
              return <MobileCarouselView contextValue={mobileContextValue} />;
            })() : (
            /* Desktop/Edit Mode - Stacked Cards Layout */
            !isSectionMode ? (() => {
              const desktopContextValue = {
                formData, handleFieldChange, errors, preventWheelChange,
                editingLoad, setErrorDialog,
                ALL_SECTIONS, desktopActiveSection, setDesktopActiveSection,
                isSectionEnabled, isSectionComplete, isSectionLocked, isSectionVisible,
                invoiceFile, setInvoiceFile, permitFile, setPermitFile, otherBills, setOtherBills,
                invoiceFile1, setInvoiceFile1, invoiceFile2, setInvoiceFile2,
                permitFile1, setPermitFile1, permitFile2, setPermitFile2,
                otherBills1, setOtherBills1, otherBills2, setOtherBills2,
                updatedInvoiceFile, setUpdatedInvoiceFile,
                additionInvoiceFile, setAdditionInvoiceFile,
                deductionInvoiceFile, setDeductionInvoiceFile,
                removePermitFile, setRemovePermitFile,
                removeUpdatedInvoiceFile, setRemoveUpdatedInvoiceFile,
                removeAdditionInvoiceFile, setRemoveAdditionInvoiceFile,
                removeDeductionInvoiceFile, setRemoveDeductionInvoiceFile,
                generating, generating1, generating2, generatingUpdated, generatingAddition, generatingDeduction,
                handleGenerateInvoiceClick, handleGenerateInvoice1Click, handleGenerateInvoice2Click,
                handleGenerateUpdatedInvoiceClick, handleGenerateAdditionInvoiceClick, handleGenerateDeductionInvoiceClick,
                handleSendEmailClick,
                invoiceFile1Ref, invoiceFile2Ref, permitFile1Ref, permitFile2Ref,
                manualInvoiceErrors, clearManualInvoiceError,
                peekedNumber, peeking, peekNextInvoiceNumber,
                deliveryAttachments, setDeliveryAttachments, existingDeliveryFiles, setExistingDeliveryFiles,
              };
              return <DesktopStackedCardsView contextValue={desktopContextValue} />;
            })() : (
            /* Section Edit Mode - Original Collapsible Layout */
            (() => {
              const sectionEditContextValue = {
                formData, handleFieldChange, errors, preventWheelChange,
                editingLoad, isSectionMode,
                shouldShowSection, isSectionEnabled, isSectionLocked, isSectionExpanded, isSectionVisible, toggleSection,
                sectionRefs,
                invoiceFile, setInvoiceFile, permitFile, setPermitFile, otherBills, setOtherBills,
                removePermitFile, setRemovePermitFile,
                generating, handleGenerateInvoiceClick, handleSendEmailClick,
                invoiceFile1, setInvoiceFile1, invoiceFile2, setInvoiceFile2,
                permitFile1, setPermitFile1, permitFile2, setPermitFile2,
                otherBills1, setOtherBills1, otherBills2, setOtherBills2,
                generating1, generating2, handleGenerateInvoice1Click, handleGenerateInvoice2Click,
                invoiceFile1Ref, invoiceFile2Ref, permitFile1Ref, permitFile2Ref,
                updatedInvoiceFile, setUpdatedInvoiceFile, removeUpdatedInvoiceFile, setRemoveUpdatedInvoiceFile,
                generatingUpdated, handleGenerateUpdatedInvoiceClick,
                additionInvoiceFile, setAdditionInvoiceFile, deductionInvoiceFile, setDeductionInvoiceFile,
                removeAdditionInvoiceFile, setRemoveAdditionInvoiceFile,
                removeDeductionInvoiceFile, setRemoveDeductionInvoiceFile,
                generatingAddition, handleGenerateAdditionInvoiceClick,
                generatingDeduction, handleGenerateDeductionInvoiceClick,
                labReportFile, setLabReportFile, removeLabReportFile, setRemoveLabReportFile,
                manualInvoiceErrors, clearManualInvoiceError,
                peekedNumber, peeking, peekNextInvoiceNumber,
                deliveryAttachments, setDeliveryAttachments, existingDeliveryFiles, setExistingDeliveryFiles,
              };
              return <SectionEditModeView contextValue={sectionEditContextValue} />;
            })()
            )
            )}

            {/* Action Buttons - Bottom with text format */}
            {(isSectionMode || !isMobile) && (
            <div className="flex gap-3 mt-4 action-buttons-container">
              <Button
                type="button"
                variant="outline"
                onClick={handleCancelClick}
                className={`rounded-xl font-semibold border-slate-300 text-slate-600 hover:bg-slate-50 h-12 px-6 ${(() => { const vs = ALL_SECTIONS.filter(s => isSectionVisible(s)); const activeSection = vs[desktopActiveSection]; return isSectionLocked(activeSection) && !isSectionMode; })() ? 'flex-1' : ''}`}
              >
                Cancel
              </Button>
              {!(() => { const vs = ALL_SECTIONS.filter(s => isSectionVisible(s)); const activeSection = vs[desktopActiveSection]; return isSectionLocked(activeSection) && !isSectionMode; })() && (() => {
                const visibleSections = ALL_SECTIONS.filter(s => isSectionVisible(s));
                const currentActiveSection = visibleSections[desktopActiveSection];
                const isCurrentSectionComplete = isSectionComplete(currentActiveSection);
                
                // Only enable Add/Update button in specific sections when they are green
                // For SVE Addition/Deduction mode, allow submit from basic section when complete
                const isSVEAdditionDeduction = formData.client_name === 'SVE CASTINGS PVT LTD' && formData.sve_load_type === 'Addition/Deduction';
                const allowedSectionsForSubmit = isSVEAdditionDeduction 
                  ? ['basic', 'billing'] 
                  : ['invoice', 'updated_invoice', 'weight_adjustment', 'billing'];
                const isAllowedSection = allowedSectionsForSubmit.includes(currentActiveSection);
                const isButtonEnabled = isSectionMode || (isAllowedSection && isCurrentSectionComplete);
                
                return (
              <Button 
                data-testid="submit-load-btn"
                type="submit" 
                disabled={isSubmitting || !isButtonEnabled}
                className={`rounded-xl font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex-1 h-12 text-white ${
                  !isButtonEnabled 
                    ? 'bg-slate-400 cursor-not-allowed'
                    : isSectionMode 
                      ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700'
                      : isCurrentSectionComplete
                        ? 'bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700' 
                        : 'bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-500 hover:to-amber-600'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  isSectionMode ? 'Save Changes' : (editingLoad?.load_id ? 'Update Load' : 'Add Load')
                )}
              </Button>
                );
              })()}
            </div>
            )}
          </form>
        </DialogContent>
      </Dialog>

      {/* Cancel Confirmation Dialog - Ask to save as draft */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-600" />
              Save as Draft?
            </AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes. Would you like to save this as a draft before closing?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              onClick={handleDiscardAndClose}
              className="w-full sm:w-auto"
            >
              No, Discard
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleSaveAsDraft}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
            >
              Yes, Save Draft
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Submit Confirmation Dialog - Confirm before adding/updating */}
      <AlertDialog open={showSubmitConfirm} onOpenChange={setShowSubmitConfirm}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Save className="w-5 h-5 text-amber-600" />
              {editingLoad?.load_id ? 'Confirm Update' : 'Confirm Add'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {editingLoad?.load_id 
                ? 'Are you sure you want to update this load?' 
                : 'Are you sure you want to add this load?'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel className="w-full sm:w-auto">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={(e) => { setShowSubmitConfirm(false); handleSubmit(e); }}
              className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-700"
            >
              {editingLoad?.load_id ? 'Yes, Update' : 'Yes, Add'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* No Changes Dialog - Edit mode with no changes */}
      <AlertDialog open={showNoChangesDialog} onOpenChange={setShowNoChangesDialog}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-slate-600" />
              Nothing to Update
            </AlertDialogTitle>
            <AlertDialogDescription>
              No changes were made to this load. Please make some changes before updating.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction 
              onClick={() => setShowNoChangesDialog(false)}
              className="w-full sm:w-auto bg-slate-600 hover:bg-slate-700"
            >
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stamp & Sign Confirmation Dialog */}
      <AlertDialog open={showStampConfirm} onOpenChange={setShowStampConfirm}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Stamp className="w-5 h-5 text-blue-600" />
              Stamp & Sign Required?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to include the official stamp and signature on the invoice?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              onClick={() => handleGenerateInvoice(false)}
              className="w-full sm:w-auto"
            >
              No, without Stamp
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleGenerateInvoice(true)}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
            >
              Yes, with Stamp & Sign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stamp & Sign Confirmation Dialog for Invoice 1 (Multi-Invoice BALAJI) */}
      <AlertDialog open={showStampConfirm1} onOpenChange={setShowStampConfirm1}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Stamp className="w-5 h-5 text-blue-600" />
              Stamp & Sign for Invoice 1?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to include the official stamp and signature on Invoice 1?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              onClick={() => handleGenerateInvoice1(false)}
              className="w-full sm:w-auto"
            >
              No, without Stamp
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleGenerateInvoice1(true)}
              className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700"
            >
              Yes, with Stamp & Sign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stamp & Sign Confirmation Dialog for Invoice 2 (Multi-Invoice BALAJI) */}
      <AlertDialog open={showStampConfirm2} onOpenChange={setShowStampConfirm2}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Stamp className="w-5 h-5 text-purple-600" />
              Stamp & Sign for Invoice 2?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to include the official stamp and signature on Invoice 2?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              onClick={() => handleGenerateInvoice2(false)}
              className="w-full sm:w-auto"
            >
              No, without Stamp
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleGenerateInvoice2(true)}
              className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700"
            >
              Yes, with Stamp & Sign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stamp & Sign Confirmation Dialog for Updated Invoice */}
      <AlertDialog open={showStampConfirmUpdated} onOpenChange={setShowStampConfirmUpdated}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Stamp className="w-5 h-5 text-amber-600" />
              Stamp & Sign Required?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to include the official stamp and signature on the updated invoice?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              onClick={() => handleGenerateUpdatedInvoice(false)}
              className="w-full sm:w-auto"
            >
              No, without Stamp
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleGenerateUpdatedInvoice(true)}
              className="w-full sm:w-auto bg-amber-600 hover:bg-amber-700"
            >
              Yes, with Stamp & Sign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stamp Confirmation Dialog for Addition Invoice (SVE) */}
      <AlertDialog open={showStampConfirmAddition} onOpenChange={setShowStampConfirmAddition}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Stamp className="w-5 h-5 text-indigo-600" />
              Stamp & Sign Required?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to include the official stamp and signature on the addition invoice?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              onClick={() => handleGenerateAdditionInvoice(false)}
              className="w-full sm:w-auto"
            >
              No, without Stamp
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleGenerateAdditionInvoice(true)}
              className="w-full sm:w-auto bg-indigo-600 hover:bg-indigo-700"
            >
              Yes, with Stamp & Sign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Stamp Confirmation Dialog for Deduction Invoice (SVE) */}
      <AlertDialog open={showStampConfirmDeduction} onOpenChange={setShowStampConfirmDeduction}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Stamp className="w-5 h-5 text-red-600" />
              Stamp & Sign Required?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Do you want to include the official stamp and signature on the deduction invoice?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              onClick={() => handleGenerateDeductionInvoice(false)}
              className="w-full sm:w-auto"
            >
              No, without Stamp
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleGenerateDeductionInvoice(true)}
              className="w-full sm:w-auto bg-red-600 hover:bg-red-700"
            >
              Yes, with Stamp & Sign
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Email Invoice Dialog */}
      <AlertDialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-purple-600" />
              Send Invoice via Email
            </AlertDialogTitle>
            <AlertDialogDescription>
              {emailInvoiceData && (
                <div className="mt-2 space-y-2">
                  <p><strong>Invoice:</strong> {emailInvoiceData.invoice_number}</p>
                  <p><strong>Amount:</strong> ₹{emailInvoiceData.invoice_amount?.toLocaleString()}</p>
                  <p><strong>Client:</strong> {emailInvoiceData.client_name}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <Label className="text-sm font-semibold">Email Address *</Label>
            <Input
              type="email"
              value={emailAddress}
              onChange={(e) => setEmailAddress(e.target.value)}
              placeholder="Enter recipient email"
              className="mt-2 h-11 rounded-xl"
            />
          </div>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel 
              onClick={() => {
                setShowEmailDialog(false);
                setEmailAddress('');
                setEmailInvoiceData(null);
              }}
              className="w-full sm:w-auto"
            >
              Cancel
            </AlertDialogCancel>
            <Button 
              onClick={handleSendEmail}
              disabled={sendingEmail || !emailAddress}
              className="w-full sm:w-auto bg-purple-600 hover:bg-purple-700 text-white"
            >
              {sendingEmail ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
